import { useState, useEffect, useRef } from "react";
import { Button, Text, Card, HStack, Progress, Spinner, Menu, Box, VStack, Editable, Clipboard, IconButton, ColorSwatch, Portal, Checkbox } from "@chakra-ui/react";
import { Cartesian3, Math as CesiumMath, Cartographic, CustomDataSource, Color, Rectangle } from "cesium";
import {
  LuImage,
  LuRoute,
  LuUpload,
  LuSettings,
  LuTrash,
  LuZoomIn,
  LuArrowBigRightDash,
  LuPen,
  LuTextCursor,
  LuEye,
  LuEyeOff,
  LuTable2,
  LuRectangleHorizontal,
  LuFilter,
  LuCheck,
  LuSave,
  LuCircle,
  LuSpline,
  LuPencilRuler,
  LuMousePointer2,
  LuFileJson2,
  LuBookCopy,
  LuDatabaseBackup,
  LuBox,
  LuCopy,
  LuRefreshCcw,
  LuDiamond,
  LuScanLine,
  LuArrowLeftRight,
  LuChartArea,
  LuMapPinMinus,
  LuMapPinPlus,
  LuShieldAlert,
  LuMapPinX,
  LuFolderSymlink,
  LuTriangleRight,
  LuSeparatorVertical,
  LuUndo,
  LuArrowRightLeft,
  LuHash,
} from "react-icons/lu";

import {
  createUniqueId,
  flyToLocation,
  getGeojsonType,
  removeFileExtension,
  formatNumberForProgress,
  saveToCache,
  getGeometryTypeGeojson,
  showToast,
  insertByPriority,
  includesNormalized,
  removeEpochSuffix,
  resetEntityFilter,
  deleteAssetsFromAsseest,
  getDataSourceGeometryType,
  createGeoJSONFile,
  bboxCornersLonLatArrays,
  matchKeyInText,
  getDataSourcePolylineMiles,
  getCombinedKeysText,
} from "../../../../../utilies/Utility";
import { toggleRightLaneBoundaryHighlight, deleteAsset as deleteAssetUtility } from "../../../../../utilies/GeojsonUtility";
import { addPolylineBetweenGeoJsonPoints, createArrowsBetweenPositions } from "../../../../../utilies/CameraImagesUtility";
import { addOSMGeojsonToMapForLaneletViewChunked, convertOSMToGeojsonForLaneletView, toggleRightLaneHighlight, deduplicateLaneletFeaturesById } from "../../../../../utilies/OsmUtility";
import Type from "../../../../../utilies/Types";
import * as JSZip from "jszip";
import { saveAs } from "file-saver";
import { Tooltip } from "./../../../../ui/tooltip";
import { serviceAddEditPostgreSQL, serviceGetLayerFromServer, serviceSaveLayer } from "../../../../../api/services/layer";
import { getEditedGeoJSON } from "../../../../../utilies/geojson/GetEditedGeojson";
import { loadGeoJSONToCesium } from "../../../../../utilies/geojson/LoadGeojson";
import { diffGeoJSON } from "../../../../../utilies/geojson/GetDiffrentGeojson";
import { toggleDirectionalArrows } from "../../../../../utilies/geojson/Arrow";
import { generateAutoLaneletRoute } from "../../../../../utilies/route/Scan";
import { load3DTiles } from "../../../../../utilies/loader/Load3DTiles";
import { loadGeojsonTypeAsync } from "../../../../../utilies/loader/LoadGeojson";
import { loadCSVTypeAsync } from "../../../../../utilies/loader/LoadCSV";
import { loadOsmTypeAsync } from "../../../../../utilies/loader/LoadOSM";
import { loadGlbTypeAsync } from "../../../../../utilies/loader/LoadGLB";
import { loadGeojsonImageType } from "../../../../../utilies/loader/LoadCamera";
import { loadCzmlTypeAsync } from "../../../../../utilies/loader/loadCzml";
import { clearLayerFilters } from "../../../../../utilies/filter/GeojsonFilter";
import { simplifyCesiumDataSource } from "../../../../../utilies/geojson/Simplify";
import { generatePointsCesiumDataSource } from "../../../../../utilies/geojson/GeneratePoint";
import { toggleLabel } from "../../../../../utilies/geojson/Label";
import { runFindClosePoints } from "../../../../../utilies/geojson/FindClosePoints";
import { serviceCreateOrEditProjects } from "../../../../../api/services/project";
import { getProjects } from "../../../../../utilies/services/project/GetProject";
import LayerImportRow from "./LayerImportRow";
import LayerSortAndSearchBar from "./LayerSortAndSearchBar";

import LayersStyleDialog from "./StyleDialog";
import LayersEdit from "./LayersEdit";
import LayersLabelDialog from "./LabelDialog";
import LayersAttributeTable from "./LayersAttributeTable";
import LayersEditFeatureDialog from "./LayersEditFeatureDialog";
import LayersDeleteDialog from "./LayersDeleteDialog";
import LayersExport from "./LayersExport";
import ImageGeojsonBoxes from "./ImageGeojsonBoxes";
import LayersFilterDialog from "./LayersFilterDialog";
import LayersCompareWith from "./LayersCompareWith";
import LayersSave from "./SavingLayer";
import LayersBultEditFeatureDialog from "./LayersBultEditFeatureDialog";
import LayersEditCoordinate from "./LayersEditCoordinate";
import CreateProjectDialog from "../project/CreateProjectDialog";
import JsonEditor from "./JsonEditor";
import LayersCopyToProject from "./LayersCopyToProject";
import LayersMoveToAnotherLayer from "./MoveToAnotherLayer";
import LayersSwitch from "./SwitchLayer";
import LayersApprovalLoadLayers from "./LayersApprovalLoadLayers";
import LayersApprovalScanRoute from "./LayersApprovalScanRoute";
import LayersChartCZML from "./LayersChartCZML";
import GeojsonFilterDialog from "./GeojsonFilterDialog";
import EditProjectDialog from "../project/EditProjectDialog";
import LayersApprovalGenerateRoute from "./LayersApprovalGenerateRoute";
import LayersRoadSlopeDialog from "./RoadSlopeDialog";
import FindBreakPoints from "./FindBreakPoints";
import LayersListFromServer from "./LayersListFromServer";
import LayersMerge from "./MergeLayers";
import LayersImportFromSQL from "./LayersImportFromSQL";
import { useAppStore } from "../../../../../context/store";
import { useMapObjects } from "../../../../../context/MapObjectsContext";
import ChangeSQLQuery from "./ChangeSQLQuery";
import { loadKmlTypeAsync } from "../../../../../utilies/loader/LoadKML";
import GeojsonFilterOnAllDialog from "./GeojsonFilterOnAllDialog";
import { createToggleAssetVisibility } from "../../../../../utilies/other/ToggleAssetVisibility";

function Layers({ droppedFiles }) {
  // ===== state =====
  const imageGeojsonMode = useAppStore((s) => s.imageGeojsonMode);
  const { mapObjects, setMapObjects } = useMapObjects();
  const imageGeojson = useAppStore((s) => s.imageGeojson);
  const imageGeojsonSelected = useAppStore((s) => s.imageGeojsonSelected);
  const editMode = useAppStore((s) => s.editMode);
  const activeLayer = useAppStore((s) => s.activeLayer);
  const currentLayerEditing = useAppStore((s) => s.currentLayerEditing);
  const defaultGeojsonStyle = useAppStore((s) => s.defaultGeojsonStyle);
  const basicGeojsonStyle = useAppStore((s) => s.basicGeojsonStyle);
  const defaultOsmStyle = useAppStore((s) => s.defaultOsmStyle);
  const projects = useAppStore((s) => s.projects);
  const userType = useAppStore((s) => s.userType);
  const userData = useAppStore((s) => s.userData);
  const loadingDataForLayers = useAppStore((s) => s.loadingDataForLayers);
  const czmlChart = useAppStore((s) => s.czmlChart);
  const userCanEditing = useAppStore((s) => s.userCanEditing);
  const showApprovalEditDialog = useAppStore((s) => s.showApprovalEditDialog);
  const simplifyToleranceSetting = useAppStore((s) => s.simplifyToleranceSetting);
  const selectedProject = useAppStore((s) => s.selectedProject);
  const mapObjectsAssest = useAppStore((s) => s.mapObjectsAssest);
  const infoboxRef = useAppStore((s) => s.infoboxRef);

  // ===== refs =====
  const viewerRef = useAppStore.getState().viewerRef;
  const originalCameraControls = useAppStore.getState().originalCameraControls;
  const directionArrowSettingRef = useAppStore.getState().directionArrowSettingRef;
  const savedHotkeysRef = useAppStore.getState().savedHotkeysRef;

  // ===== setters =====
  const setImageGeojsonMode = useAppStore.getState().setImageGeojsonMode;
  const setImageGeojson = useAppStore.getState().setImageGeojson;
  const setImageGeojsonSelected = useAppStore.getState().setImageGeojsonSelected;
  const setImageDataSource = useAppStore.getState().setImageDataSource;
  const setImageGeojsonCounter = useAppStore.getState().setImageGeojsonCounter;
  const setGridMode = useAppStore.getState().setGridMode;
  const setMeasureDistanceMode = useAppStore.getState().setMeasureDistanceMode;
  const setShowStyleDialog = useAppStore.getState().setShowStyleDialog;
  const setEditMode = useAppStore.getState().setEditMode;
  const setShowLabelDialog = useAppStore.getState().setShowLabelDialog;
  const setGeojsonImageCamera = useAppStore.getState().setGeojsonImageCamera;
  const setActiveLayer = useAppStore.getState().setActiveLayer;
  const setCurrentLayerEditing = useAppStore.getState().setCurrentLayerEditing;
  const setCurrentLayer = useAppStore.getState().setCurrentLayer;
  const setCurrentLayerForJsonEditor = useAppStore.getState().setCurrentLayerForJsonEditor;

  const setShowSwitchLayerDialog = useAppStore.getState().setShowSwitchLayerDialog;
  const setShowQueryTableDialog = useAppStore.getState().setShowQueryTableDialog;
  const setLoadedPorject = useAppStore.getState().setLoadedPorject;
  const setDeleteDialog = useAppStore.getState().setDeleteDialog;
  const setExportDialog = useAppStore.getState().setExportDialog;
  const setFilterDialog = useAppStore.getState().setFilterDialog;
  const setShowCompareDialog = useAppStore.getState().setShowCompareDialog;
  const setShowLayersSaveDialog = useAppStore.getState().setShowLayersSaveDialog;
  const setAutoLayersSave = useAppStore.getState().setAutoLayersSave;
  const setImportUrls = useAppStore.getState().setImportUrls;
  const setProjects = useAppStore.getState().setProjects;
  const setSelectedProject = useAppStore.getState().setSelectedProject;

  const pointCloudStyle = useAppStore((s) => s.pointCloudStyle);

  const setShowJsonEditor = useAppStore.getState().setShowJsonEditor;
  const setCamera = useAppStore.getState().setCamera;
  const setIsShowCopyToProjectDialog = useAppStore.getState().setIsShowCopyToProjectDialog;
  const setLayersApprovalLoadLayerDialog = useAppStore.getState().setLayersApprovalLoadLayerDialog;
  const setLayersApprovalScanRouteDialog = useAppStore.getState().setLayersApprovalScanRouteDialog;
  const setCzmlChart = useAppStore.getState().setCzmlChart;
  const setFilterGeojsonDialog = useAppStore.getState().setFilterGeojsonDialog;
  const setCurrentLayerForAttributeTable = useAppStore.getState().setCurrentLayerForAttributeTable;
  const setMapObjectsAssest = useAppStore.getState().setMapObjectsAssest;
  const setShowRoadSlopeDialog = useAppStore.getState().setShowRoadSlopeDialog;
  const setShowZigzagDialog = useAppStore.getState().setShowZigzagDialog;
  const setIsOpenAddonsMenu = useAppStore.getState().setIsOpenAddonsMenu;

  const setShowChangeSQLDialog = useAppStore.getState().setShowChangeSQLDialog;

  const setUserType = useAppStore.getState().setUserType;
  const setUserCanEditing = useAppStore.getState().setUserCanEditing;

  const [uploadKey, setUploadKey] = useState(0);
  const [selectedImportType, setSelectedImportType] = useState(Type.ASSET.THREEDTILES);
  const [isAZ, setIsAZ] = useState(true);
  const [filterType, setFilterType] = useState("priority");
  const [isShowAllLayers, setIsShowAllLayers] = useState(true);
  const [layerSearch, setLayerSearch] = useState("");

  const [selectedObjectsForDelete, setSelectedObjectsForDelete] = useState([]);

  // 1. First way to load objects: Drag and Drop File
  useEffect(() => {
    if (!droppedFiles[0] || droppedFiles[0].length === 0) return;

    droppedFiles[0].forEach(async (file) => {
      let fileFormat = "none";
      const fileName = file.name.toLowerCase();

      if (fileName.endsWith(".geojson")) {
        fileFormat = "geojson";
      } else if (fileName.endsWith(".glb")) {
        fileFormat = "glb";
      } else if (fileName.endsWith(".json")) {
        fileFormat = "json";
      } else if (fileName.endsWith(".zip")) {
        fileFormat = "zip";
      } else if (fileName.endsWith(".osm")) {
        fileFormat = "osm";
      } else if (fileName.endsWith(".csv")) {
        fileFormat = "csv";
      } else if (fileName.endsWith(".czml")) {
        fileFormat = "czml";
      }

      switch (fileFormat) {
        case "geojson":
          try {
            const sizeInBytes = file.size || new TextEncoder().encode(file.content).length;
            const sizeInMB = (sizeInBytes / (1024 * 1024)).toFixed(2);

            const geoJsonData = JSON.parse(file.content);
            switch (getGeojsonType(geoJsonData)) {
              case Type.ASSET.GEOJSON_IMAGE:
                loadGeojsonImageType({
                  viewerRef,
                  setImageGeojson,
                  setImageGeojsonSelected,
                  setImageGeojsonMode,
                  setImageDataSource,
                  setMapObjects,
                  setImageGeojsonCounter,
                  geojsonData: geoJsonData,
                  fileName: file.name,
                });

                break;
              case Type.ASSET.GEOJSON:
                if (parseFloat(sizeInMB) >= 10) {
                  setLayersApprovalLoadLayerDialog({
                    originalCameraControls,
                    geojsonData: geoJsonData,
                    fileName: file.name,
                    viewerRef,
                    setMapObjects,
                    defaultGeojsonStyle,
                    basicGeojsonStyle,
                    defaultOsmStyle,
                  });
                } else {
                  await loadGeojsonTypeAsync({
                    originalCameraControls,
                    geojsonData: geoJsonData,
                    fileName: file.name,
                    viewerRef,
                    setMapObjects,
                    defaultGeojsonStyle,
                    basicGeojsonStyle,
                    defaultOsmStyle,
                  });
                }

                break;
            }
          } catch (error) {
            console.log("Error parsing geojson:", file.name, error);
          }
          break;

        case "glb":
          try {
            loadGlbTypeAsync({
              arrayBuffer: file.content,
              fileName: file.name,
              viewerRef,
              setMapObjects,
              positionDegrees: { lon: -112.068096, lat: 33.450842, height: 0 },
              orientationDegrees: { heading: 0, pitch: 0, roll: 0 },
              scale: 1.0,
            });
          } catch (error) {
            console.log("Error loading glb:", file.name, error);
          }
          break;

        case "zip":
          try {
            JSZip.loadAsync(file.content)
              .then(async (zip) => {
                for (const relativePath in zip.files) {
                  const zipFile = zip.files[relativePath];
                  if (!zipFile.dir) {
                    try {
                      const fileData = await zipFile.async("string");

                      if (zipFile.name.endsWith(".geojson")) {
                        await loadGeojsonTypeAsync({
                          originalCameraControls,
                          geojsonData: JSON.parse(fileData),
                          fileName: zipFile.name,
                          viewerRef,
                          setMapObjects,
                          defaultGeojsonStyle,
                          basicGeojsonStyle,
                          defaultOsmStyle,
                        });
                      } else if (zipFile.name.endsWith(".glb")) {
                        loadGlbTypeAsync({
                          arrayBuffer: fileData,
                          fileName: zipFile.name,
                          viewerRef,
                          setMapObjects,
                          positionDegrees: { lon: -112.068096, lat: 33.450842, height: 0 },
                          orientationDegrees: { heading: 0, pitch: 0, roll: 0 },
                          scale: 1.0,
                        });
                      }
                    } catch (err) {
                      console.log("Error reading zip entry:", err);
                    }
                  }
                }
              })
              .catch((error) => {
                console.log("Zip load error:", error);
              });
          } catch (error) {
            console.log("Zip handling error:", error);
          }
          break;

        case "osm":
          try {
            const encoder = new TextEncoder();
            const arrayBuffer = encoder.encode(file.content).buffer;
            await loadOsmTypeAsync({
              arrayBuffer: arrayBuffer,
              fileName: file.name,
              viewerRef,
              setMapObjects,
              setActiveLayer,
              defaultOsmStyle,
              fileFormat: "osm",

              defaultGeojsonStyle,
              basicGeojsonStyle,
            });
          } catch (error) {
            console.log("OSM loading error:", error);
          }
          break;

        case "csv":
          try {
            await loadCSVTypeAsync({
              text: file.content,
              fileName: file.name,
              viewerRef,
              setMapObjects,
              defaultGeojsonStyle,
            });
          } catch (error) {
            console.log("OSM loading error:", error);
          }
          break;

        case "czml":
          try {
            let text;

            // if Blob or File
            if (file.content instanceof Blob) {
              text = await file.content.text();
            }
            // if ArrayBuffer
            else if (file.content instanceof ArrayBuffer) {
              text = new TextDecoder().decode(file.content);
            }
            // if plain string
            else {
              text = file.content;
            }

            const czmlData = JSON.parse(text);

            await loadCzmlTypeAsync({
              czmlData,
              fileName: file.name,
              viewerRef,
              setMapObjects,
            });
          } catch (error) {
            console.log("Error loading czml:", file.name, error);
          }
          break;
        default:
          console.log("Unsupported file format:", file.name);
          break;
      }
    });
    droppedFiles[1]([]);
  }, [droppedFiles[0]]);

  // 3. Third way to load objects: From URL
  const importFromUrl = async (inputUrl = null, inputType = null) => {
    let localUrl = inputUrl;
    let localType = inputType ? inputType : selectedImportType;

    try {
      switch (localType) {
        case Type.ASSET.THREEDTILES:
          await load3DTiles({ url: localUrl, viewerRef, setMapObjects });
          break;
        case Type.ASSET.GEOJSON_IMAGE:
          fetch(localUrl)
            .then((response) => {
              if (!response.ok) {
              }
              return response.json();
            })
            .then((data) => {
              let geoJsonData = data;
              loadGeojsonImageType({
                viewerRef,
                setImageGeojson,
                setImageGeojsonSelected,
                setImageGeojsonMode,
                setImageDataSource,
                setMapObjects,
                setImageGeojsonCounter,
                geojsonData: geoJsonData,
                fileName: localUrl,
              });
            })
            .catch((error) => {});
          break;
        case Type.ASSET.GEOJSON:
          fetch(localUrl)
            .then((response) => {
              if (!response.ok) {
              }
              return response.json();
            })
            .then((data) => {
              let geoJsonData = data;

              loadGeojsonTypeAsync({
                originalCameraControls,
                geojsonData: geoJsonData,
                fileName: localUrl,
                viewerRef,
                setMapObjects,
                defaultGeojsonStyle,
                basicGeojsonStyle,
                isUrl: true,
                defaultOsmStyle,
                projectId: selectedProject?.id,
                selectedProject,
                userData,
                pointCloudStyle,
                setProjects,
              });
            })
            .catch((error) => {});
          break;
        case Type.ASSET.CSV:
          fetch(localUrl)
            .then((response) => {
              if (!response.ok) {
                throw new Error("Network response was not ok");
              }
              return response.text();
            })
            .then(async (csvText) => {
              await loadCSVTypeAsync({
                text: csvText,
                fileName: localUrl,
                viewerRef,
                setMapObjects,
                defaultGeojsonStyle,
              });
            })
            .catch((error) => {
              console.error("Error loading CSV:", error);
            });
          break;
        case Type.ASSET.OSM:
          fetch(localUrl)
            .then((response) => {
              if (!response.ok) {
                throw new Error("Network response was not ok");
              }
              return response.text();
            })
            .then((text) => {
              const encoder = new TextEncoder();
              const arrayBuffer = encoder.encode(text).buffer;
              loadOsmTypeAsync({
                arrayBuffer: arrayBuffer,
                fileName: localUrl,
                viewerRef,
                setMapObjects,
                setActiveLayer,
                defaultOsmStyle,
                fileFormat: "osm",

                defaultGeojsonStyle,
                basicGeojsonStyle,
              });
            })
            .catch((error) => {
              console.error("Fetch error:", error);
            });
          break;
        case Type.ASSET.ZIP:
          fetch(localUrl)
            .then((response) => {
              if (!response.ok) {
                throw new Error("Failed to fetch zip");
              }
              return response.arrayBuffer();
            })
            .then((zipData) => JSZip.loadAsync(zipData))
            .then(async (zip) => {
              for (const relativePath in zip.files) {
                const file = zip.files[relativePath];
                if (!file.dir) {
                  try {
                    const fileData = await file.async("string");

                    if (file.name.endsWith(".geojson")) {
                      loadGeojsonTypeAsync({
                        originalCameraControls,
                        geojsonData: JSON.parse(fileData),
                        fileName: file.name,
                        viewerRef,
                        setMapObjects,
                        defaultGeojsonStyle,
                        basicGeojsonStyle,
                        defaultOsmStyle,
                      });
                    } else if (file.name.endsWith(".glb")) {
                      const glbBuffer = await file.async("arraybuffer");
                      loadGlbTypeAsync({
                        arrayBuffer: glbBuffer,
                        fileName: file.name,
                        viewerRef,
                        setMapObjects,
                        positionDegrees: { lon: -112.068096, lat: 33.450842, height: 0 },
                        orientationDegrees: { heading: 0, pitch: 0, roll: 0 },
                        scale: 1.0,
                      });
                    } else if (file.name.endsWith(".kml")) {
                      const kmlText = await file.async("string");
                      await loadKmlTypeAsync({
                        kmlText,
                        fileName: file.name,
                        viewerRef,
                        setMapObjects,
                        clampToGround: true,
                      });
                    } else if (file.name.endsWith(".kmz")) {
                      const kmzBuffer = await file.async("arraybuffer");
                      await loadKmlTypeAsync({
                        fileOrBlob: new Blob([kmzBuffer], { type: "application/vnd.google-earth.kmz" }),
                        fileName: file.name,
                        viewerRef,
                        setMapObjects,
                        clampToGround: true,
                      });
                    }
                  } catch (err) {
                    console.error("Error reading file:", file.name, err);
                  }
                }
              }
            })
            .catch((error) => {
              console.error("Error loading zip file:", error);
            });
          break;
        case Type.ASSET.GLB:
          fetch(localUrl)
            .then((response) => {
              if (!response.ok) {
                throw new Error("Failed to fetch GLB file");
              }
              return response.arrayBuffer();
            })
            .then((glbData) => {
              loadGlbTypeAsync({
                arrayBuffer: glbData,
                fileName: localUrl,
                viewerRef,
                setMapObjects,
                positionDegrees: { lon: -112.068096, lat: 33.450842, height: 0 },
                orientationDegrees: { heading: 0, pitch: 0, roll: 0 },
                scale: 1.0,
              });
            })
            .catch((error) => {
              console.error("Error loading GLB file:", error);
            });

          break;
        case Type.ASSET.KML:
          fetch(localUrl)
            .then((response) => {
              if (!response.ok) throw new Error("Failed to fetch KML");
              return response.text();
            })
            .then(async (kmlText) => {
              await loadKmlTypeAsync({
                kmlText,
                fileName: localUrl,
                viewerRef,
                setMapObjects,
                clampToGround: true,
                isUrl: true,
                url: localUrl,
              });
            })
            .catch((error) => {
              console.error("Error loading KML:", error);
            });
          break;

        case Type.ASSET.KMZ:
          fetch(localUrl)
            .then((response) => {
              if (!response.ok) throw new Error("Failed to fetch KMZ");
              return response.arrayBuffer();
            })
            .then(async (kmzBuf) => {
              await loadKmlTypeAsync({
                fileOrBlob: new Blob([kmzBuf], { type: "application/vnd.google-earth.kmz" }),
                fileName: localUrl,
                viewerRef,
                setMapObjects,
                clampToGround: true,
                isUrl: true,
                url: localUrl,
              });
            })
            .catch((error) => {
              console.error("Error loading KMZ:", error);
            });
          break;
      }
      const newImport = { url: localUrl, type: localType };

      setImportUrls((prev) => {
        const isDuplicate = prev.some((item) => item.url === newImport.url && item.type === newImport.type);

        if (isDuplicate) {
          return prev;
        }

        const updated = [...prev, newImport];
        saveToCache(Type.STORAGE.IMPORT_URLS, updated);
        return updated;
      });
    } catch (error) {
      console.error(`Error creating tileset: ${error}`);
    }
  };

  // Handle keyboard
  useEffect(() => {
    const isMod = (k) => ["shift", "ctrl", "alt", "meta"].includes(String(k).toLowerCase());
    const isAlpha = (k) => /^[a-z]$/i.test(String(k || ""));
    const normalizeKey = (k) => {
      let key = String(k).toLowerCase();
      if (key === "control") key = "ctrl";
      if (key === " ") key = "space";
      return key;
    };

    const getCombo = (type) => savedHotkeysRef.current.find((h) => h.type === type)?.combination || [];

    const matches = (e, combo) => {
      if (!combo || combo.length === 0) return false;

      const norm = combo.map(normalizeKey);
      const want = {
        shift: norm.includes("shift"),
        ctrl: norm.includes("ctrl"),
        alt: norm.includes("alt"),
        meta: norm.includes("meta"),
      };

      if (e.shiftKey !== want.shift) return false;
      if (e.ctrlKey !== want.ctrl) return false;
      if (e.altKey !== want.alt) return false;
      if (e.metaKey !== want.meta) return false;

      const main = norm.find((k) => !isMod(k));
      if (!main) return true;

      const pressed = normalizeKey(e.key);
      return pressed === main;
    };

    // -------------------------
    // 3-key sequential support
    // combo: [mod, letter1, letter2]
    // user: mod+letter1, then within 600ms press letter2 (with same mod or no mod)
    // -------------------------
    const PENDING_KEY = "__global_hotkey_pending__";
    if (!window[PENDING_KEY]) {
      window[PENDING_KEY] = { active: false, mods: [], stepKey: "", options: {}, expiresAt: 0, tid: null };
    }
    const pending = window[PENDING_KEY];

    const resetPending = () => {
      if (pending.tid) clearTimeout(pending.tid);
      pending.active = false;
      pending.mods = [];
      pending.stepKey = "";
      pending.options = {};
      pending.expiresAt = 0;
      pending.tid = null;
    };

    const modsMatchExactly = (e, mods) => {
      const needShift = mods.includes("shift");
      const needCtrl = mods.includes("ctrl");
      const needAlt = mods.includes("alt");
      const needMeta = mods.includes("meta");

      if (e.shiftKey !== needShift) return false;
      if (e.ctrlKey !== needCtrl) return false;
      if (e.altKey !== needAlt) return false;
      if (e.metaKey !== needMeta) return false;
      return true;
    };

    const modsAreNone = (e) => !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey;

    const isSequential3Combo = (combo) => {
      if (!combo || combo.length !== 3) return false;

      const norm = combo.map(normalizeKey);
      const mods = norm.filter(isMod);
      const nonMods = norm.filter((k) => !isMod(k));

      return mods.length === 1 && nonMods.length === 2 && isAlpha(nonMods[0]) && isAlpha(nonMods[1]);
    };

    const getSequentialParts = (combo) => {
      const norm = combo.map(normalizeKey);
      const mods = norm.filter(isMod);
      const nonMods = norm.filter((k) => !isMod(k));
      return { mods, stepKey: nonMods[0], finalKey: nonMods[1] };
    };

    const startPending = (mods, stepKey, finalKey) => {
      resetPending();
      pending.active = true;
      pending.mods = mods;
      pending.stepKey = stepKey;
      pending.finalKey = finalKey;
      pending.expiresAt = Date.now() + 3000;
      pending.tid = setTimeout(() => resetPending(), 3000);
    };

    const runHotkey = (type, event) => {
      // NOTE: this function keeps your original logic untouched, just moved into a dispatcher.
      if (type === Type.HOTKEYS.SELECT_LANELET) {
        event.preventDefault();
        for (const key in mapObjects) {
          if (includesNormalized(mapObjects[key].file_name, "lanelet")) {
            setActiveLayer(mapObjects[key].id);
            break;
          }
        }
        return true;
      }

      if (type === Type.HOTKEYS.TOGGLE_SOME_VISIBILITY) {
        event.preventDefault();
        for (const key in mapObjects) {
          if (
            includesNormalized(mapObjects[key].file_name, "lanelet") ||
            includesNormalized(mapObjects[key].file_name, "virtualline") ||
            includesNormalized(mapObjects[key].file_name, "stopline") ||
            includesNormalized(mapObjects[key].file_name, "yieldline") ||
            includesNormalized(mapObjects[key].file_name, "trafficsignbox") ||
            includesNormalized(mapObjects[key].file_name, "trafficlightbox") ||
            includesNormalized(mapObjects[key].file_name, "roadedge") ||
            includesNormalized(mapObjects[key].file_name, "turningline") ||
            includesNormalized(mapObjects[key].file_name, "curbstone") ||
            includesNormalized(mapObjects[key].file_name, "lanemarker")
          ) {
          } else {
            toggleAssetVisibility(mapObjects[key], false);
          }
        }
        return true;
      }

      if (type === Type.HOTKEYS.TOGGLE_ROUTE_VISIBILITY) {
        event.preventDefault();
        for (const key in mapObjects) {
          if (
            removeEpochSuffix(removeFileExtension(mapObjects[key].file_name.toLowerCase())).startsWith("route") ||
            removeEpochSuffix(removeFileExtension(mapObjects[key].file_name.toLowerCase())).startsWith("rt")
          ) {
            toggleAssetVisibility(mapObjects[key], false);
          }
        }
        return true;
      }

      if (type === Type.HOTKEYS.TOGGLE_LANE_MARKER_EDIT) {
        event.preventDefault();
        if (!(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN))) return true;
        if (editMode) return true;
        if (!userCanEditing) return true;

        let found = false;
        for (const key in mapObjects) {
          if (removeEpochSuffix(removeFileExtension(mapObjects[key].file_name.toLowerCase())).includes("lane_marker")) {
            setActiveLayer(mapObjects[key].id);
            editLayer(mapObjects[key]);
            found = true;
            break;
          }
        }

        return found;
      }

      if (type === Type.HOTKEYS.TOGGLE_VIRTUAL_LAYER_EDIT) {
        event.preventDefault();
        if (!(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN))) return true;
        if (editMode) return true;
        if (!userCanEditing) return true;

        let found = false;
        for (const key in mapObjects) {
          if (removeEpochSuffix(removeFileExtension(mapObjects[key].file_name.toLowerCase())).includes("virtual_line")) {
            setActiveLayer(mapObjects[key].id);
            editLayer(mapObjects[key]);
            found = true;
            break;
          }
        }

        return found;
      }

      if (type === Type.HOTKEYS.TOGGLE_ROAD_EDGE_EDIT) {
        event.preventDefault();
        if (!(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN))) return true;
        if (editMode) return true;
        if (!userCanEditing) return true;
        for (const key in mapObjects) {
          if (removeEpochSuffix(removeFileExtension(mapObjects[key].file_name.toLowerCase())).includes("road_edge")) {
            setActiveLayer(mapObjects[key].id);
            editLayer(mapObjects[key]);
            break;
          }
        }
        return true;
      }

      if (type === Type.HOTKEYS.TOGGLE_TRAFFIC_SIGN_BOX_EDIT) {
        event.preventDefault();
        if (!(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN))) return true;
        if (editMode) return true;
        if (!userCanEditing) return true;
        for (const key in mapObjects) {
          if (removeEpochSuffix(removeFileExtension(mapObjects[key].file_name.toLowerCase())).includes("traffic_sign_box")) {
            setActiveLayer(mapObjects[key].id);
            editLayer(mapObjects[key]);
            break;
          }
        }
        return true;
      }

      if (!(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN))) return false;

      if (type === Type.HOTKEYS.TOGGLE_STOP_LINE_EDIT) {
        event.preventDefault();
        if (editMode) return true;
        if (!userCanEditing) return true;
        for (const key in mapObjects) {
          if (removeEpochSuffix(removeFileExtension(mapObjects[key].file_name.toLowerCase())).includes("stop_line")) {
            setActiveLayer(mapObjects[key].id);
            editLayer(mapObjects[key]);
            break;
          }
        }
        return true;
      }

      if (type === Type.HOTKEYS.TOGGLE_CURBSTONE_EDIT) {
        event.preventDefault();
        if (editMode) return true;
        if (!userCanEditing) return true;
        for (const key in mapObjects) {
          if (removeEpochSuffix(removeFileExtension(mapObjects[key].file_name.toLowerCase())).includes("curbstone")) {
            setActiveLayer(mapObjects[key].id);
            editLayer(mapObjects[key]);
            break;
          }
        }
        return true;
      }

      // --- 2) Require active layer ---
      if (!activeLayer) return true;

      const selected = Object.values(mapObjects).find((obj) => obj.id === activeLayer);
      if (!selected) return true;

      if (type === Type.HOTKEYS.DELETE_LAYER) {
        event.preventDefault();
        deleteAsset(selected);
        return true;
      }

      if (type === Type.HOTKEYS.TOGGLE_LABEL_VISIBILITY) {
        event.preventDefault();
        toggleLabelVisibility(selected);
        return true;
      }

      if (type === Type.HOTKEYS.TOGGLE_LAYER_VISIBILITY) {
        event.preventDefault();
        toggleAssetVisibility(selected);
        return true;
      }

      if (type === Type.HOTKEYS.ZOOM_TO_EXTENT) {
        event.preventDefault();
        zoomToExtend(selected);
        return true;
      }

      if (type === Type.HOTKEYS.SAVE_LAYER_TO_SERVER) {
        event.preventDefault();
        if (!(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN))) return true;
        setCurrentLayer(selected);
        setShowLayersSaveDialog(true);
        return true;
      }

      if (type === Type.HOTKEYS.OPEN_SEARCH_GEOJSON) {
        event.preventDefault();
        setCurrentLayer(selected);
        setFilterGeojsonDialog(true);
        return true;
      }

      if (type === Type.HOTKEYS.RESET_SEARCH_GEOJSON) {
        event.preventDefault();
        clearLayerFilters(selected, mapObjects, setMapObjects, resetEntityFilter);
        return true;
      }

      if (type === Type.HOTKEYS.SAVE_TO_DATABASE) {
        event.preventDefault();
        if (!(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN))) return true;
        if (!userCanEditing) return true;
        saveToDatabse(selected);
        return true;
      }

      if (type === Type.HOTKEYS.TOGGLE_DIRECTION_VISIBILITY) {
        event.preventDefault();
        toggleDirectionalArrows(viewerRef.current, selected.dataSource, !selected.arrow_visibility, directionArrowSettingRef);
        setMapObjects((prev) => prev.map((o) => (o.id === selected.id ? { ...o, arrow_visibility: !o.arrow_visibility } : o)));
        return true;
      }

      if (type === Type.HOTKEYS.ACTIVE_EDIT_LAYER) {
        event.preventDefault();
        if (!(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN))) return true;
        if (!userCanEditing) return true;
        editLayer(selected);
        return true;
      }

      return false;
    };

    const handleKeyDown = (event) => {
      // 1) If pending, check final key first
      if (pending.active) {
        if (Date.now() > pending.expiresAt) {
          resetPending();
        } else {
          const k = normalizeKey(event.key);
          const okMods = modsMatchExactly(event, pending.mods) || modsAreNone(event);

          if (okMods && pending.options && pending.options[k]) {
            const type = pending.options[k];
            event.preventDefault();
            resetPending();
            runHotkey(type, event);
            return;
          }

          if (!isMod(k)) resetPending();
        }
      }

      // 2) Process hotkeys in the same order you had (first match wins)
      const orderedTypes = [
        Type.HOTKEYS.SELECT_LANELET,
        Type.HOTKEYS.TOGGLE_SOME_VISIBILITY,
        Type.HOTKEYS.TOGGLE_ROUTE_VISIBILITY,
        Type.HOTKEYS.TOGGLE_LANE_MARKER_EDIT,
        Type.HOTKEYS.TOGGLE_VIRTUAL_LAYER_EDIT,
        Type.HOTKEYS.TOGGLE_ROAD_EDGE_EDIT,
        Type.HOTKEYS.TOGGLE_TRAFFIC_SIGN_BOX_EDIT,
        Type.HOTKEYS.TOGGLE_STOP_LINE_EDIT,
        Type.HOTKEYS.TOGGLE_CURBSTONE_EDIT,
        Type.HOTKEYS.DELETE_LAYER,
        Type.HOTKEYS.TOGGLE_LABEL_VISIBILITY,
        Type.HOTKEYS.TOGGLE_LAYER_VISIBILITY,
        Type.HOTKEYS.ZOOM_TO_EXTENT,
        Type.HOTKEYS.SAVE_LAYER_TO_SERVER,
        Type.HOTKEYS.OPEN_SEARCH_GEOJSON,
        Type.HOTKEYS.RESET_SEARCH_GEOJSON,
        Type.HOTKEYS.SAVE_TO_DATABASE,
        Type.HOTKEYS.TOGGLE_DIRECTION_VISIBILITY,
        Type.HOTKEYS.ACTIVE_EDIT_LAYER,
      ];

      for (const type of orderedTypes) {
        const combo = getCombo(type);

        // sequential 3-key handling
        if (isSequential3Combo(combo)) {
          const { mods, stepKey } = getSequentialParts(combo);

          if (modsMatchExactly(event, mods) && normalizeKey(event.key) === normalizeKey(stepKey)) {
            event.preventDefault();
            resetPending();

            const options = {};
            for (const t2 of orderedTypes) {
              const c2 = getCombo(t2);
              if (!isSequential3Combo(c2)) continue;

              const p2 = getSequentialParts(c2);
              if (!modsMatchExactly(event, p2.mods)) continue;
              if (normalizeKey(p2.stepKey) !== normalizeKey(stepKey)) continue;

              options[normalizeKey(p2.finalKey)] = t2;
            }

            pending.active = true;
            pending.mods = mods;
            pending.stepKey = stepKey;
            pending.options = options;
            pending.expiresAt = Date.now() + 3000;
            pending.tid = setTimeout(() => resetPending(), 3000);
            return;
          }
        }

        // normal one-shot
        if (matches(event, combo)) {
          resetPending();
          const handled = runHotkey(type, event);
          if (handled) return;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      resetPending();
    };
  }, [activeLayer, mapObjects, viewerRef, editMode, showApprovalEditDialog]);

  const saveToDatabse = (item) => {
    if (item?.is_load_from_database && item?.is_edited) {
      // original diff
      const dif = diffGeoJSON(item.original_geojson, getEditedGeoJSON(item));

      // deep copy and remove properties.id only for adds
      const difCopy = dif.map((c) => {
        // make deep copy
        const copy = JSON.parse(JSON.stringify(c));

        if (copy.type === "add" && copy.properties) {
          // explicitly delete id key if exists
          if ("id" in copy.properties) delete copy.properties.id;
        }

        return copy;
      });

      // send copy, keep original untouched
      const formData = new FormData();
      formData.append(
        "data",
        JSON.stringify({
          table: item.sql_table,
          schema: item.sql_schema,
          changes: difCopy,
        }),
      );

      serviceAddEditPostgreSQL(formData, userData.token)
        .then((res) => {
          if (res.data.status == "success") {
            let addedIds = [];
            for (const key in res.data.result.added) {
              for (const key2 in res.data.result.added[key]) {
                const v = res.data.result.added[key][key2];
                addedIds.push(v);
                for (const key3 in dif) {
                  const d = dif[key3];
                  if (d.ref_id == key2) {
                    viewerRef.current.dataSources._dataSources.forEach((ds) => {
                      const entities = ds.entities.values;
                      for (const entity of entities) {
                        const props = entity.properties?.getValue?.() || entity.properties;
                        if (props?.id === d.properties.id) {
                          entity.properties.id = v;
                        }
                      }
                    });
                  }
                }
              }
            }

            let modifiedIds = [];
            for (const key in res.data.result.modified) {
              const v = res.data.result.modified[key];
              modifiedIds.push(v);
            }
            let deletedIds = [];
            for (const key in res.data.result.deleted) {
              const v = res.data.result.deleted[key];
              deletedIds.push(v);
            }

            if (addedIds.length > 0) showToast("Added: " + addedIds, Type.NOTIFICATION.SUCCESS, { duration: 60000, closable: true });
            if (modifiedIds.length > 0) showToast("Modified: " + modifiedIds, Type.NOTIFICATION.SUCCESS, { duration: 60000, closable: true });
            if (deletedIds.length > 0) showToast("Deleted: " + deletedIds, Type.NOTIFICATION.SUCCESS, { duration: 60000, closable: true });
            if (res.data.result.rejected?.length > 0) showToast(JSON.stringify(res.data.result.rejected), Type.NOTIFICATION.WARNING, { duration: 60000, closable: true });
            setMapObjects((prev) =>
              prev.map((o) =>
                o.id === item.id
                  ? {
                      ...o,
                      original_geojson: getEditedGeoJSON(item),
                      is_edited: false,
                    }
                  : o,
              ),
            );
          } else {
            showToast(res.data.message, Type.NOTIFICATION.WARNING);
          }
        })
        .catch((err) => {
          showToast(err.message, Type.NOTIFICATION.ERROR);
          console.error("Error", err);
        });
    }
  };

  const cleanMap = () => {
    setLayerSearch("");
    setSelectedObjectsForDelete([]);
    if (selectedProject?.id) {
      let temp2 = selectedProject.contributors;

      if (temp2[0]?.username) {
        for (const key in temp2) {
          if (temp2[key].username == userData.user.username) {
            setUserType(userData.roles);

            if (
              userData.roles.includes(Type.ROLE.ADMIN) ||
              userData.roles.includes(Type.ROLE.ANNOTATOR) ||
              userData.roles.includes(Type.ROLE.DIGITIZER) ||
              userData.roles.includes(Type.ROLE.SUPER_ADMIN)
            ) {
              setUserCanEditing(true);
            } else {
              setUserCanEditing(false);
            }
            break;
          }
        }
      }
    }

    setShowQueryTableDialog(false);
    setSelectedProject(null);
    setLoadedPorject([]);

    let temp = [...projects];
    for (const key in temp) {
      temp[key].config_file_data = null;
    }
    setProjects(temp);

    for (const moKey in mapObjects) {
      const objectToRemove = mapObjects[moKey];
      deleteAsset(objectToRemove, true);
    }
    getProjects({
      userData,
      setProjects,
    });

    deleteAssetsFromAsseest(viewerRef.current, null, setMapObjectsAssest, setLoadedPorject, true);
  };

  // toggle label visibility and keep it
  const toggleLabelVisibility = async (item) => {
    const viewer = viewerRef.current;
    if (!viewer || !item?.dataSource) return;

    const nextVisible = !item.label_visible;

    // entities of this layer
    const entities = item.dataSource.entities?.values || [];
    if (!entities.length) {
      // no entities, just flip state
      setMapObjects((prev) => prev.map((o) => (o.id === item.id ? { ...o, label_visible: nextVisible } : o)));

      return;
    }

    // label config from layer state
    const keys = Array.isArray(item.selected_tags_for_label) ? item.selected_tags_for_label : [];
    const textSize = Number(item.label_text_size || 13);
    const textColor = item.label_text_color || "white";
    const textOffset = item.label_offset || 2;

    if (!keys.length) {
      setMapObjects((prev) => prev.map((o) => (o.id === item.id ? { ...o, label_visible: nextVisible } : o)));

      return;
    }

    // rebuild with new visibility so internal baseOpts.visible is updated
    toggleLabel({
      viewer,
      dataSource: item.dataSource,
      visible: nextVisible,
      keys,
      textSize,
      textColor,
      show: nextVisible,
      offset: textOffset,
    });

    if (viewer.scene?.requestRenderMode) viewer.scene.requestRender();

    setMapObjects((prev) => prev.map((o) => (o.id === item.id ? { ...o, label_visible: nextVisible } : o)));
  };

  const zoomToExtend = (item) => {
    if (!item || !viewerRef?.current) return; // Prevent null/undefined crash

    switch (item.type) {
      case Type.ASSET.GEOJSON_IMAGE: {
        // Ensure imageGeojsonSelected and geometry exist
        if (imageGeojsonSelected?.geometry?.coordinates && Array.isArray(imageGeojsonSelected.geometry.coordinates) && imageGeojsonSelected.geometry.coordinates.length >= 2) {
          const coords = imageGeojsonSelected.geometry.coordinates;
          flyToLocation(viewerRef.current, coords[0], coords[1], null);
        } else {
          console.warn("Invalid imageGeojsonSelected geometry:", imageGeojsonSelected);
        }
        break;
      }

      case Type.ASSET.THREEDTILES: {
        // Ensure imageGeojsonSelected and geometry exist
        viewerRef.current.zoomTo(item.tileset);
        break;
      }

      default: {
        if (!mapObjects) return; // Prevent crash if mapObjects is null/undefined

        for (const key in mapObjects) {
          const obj = mapObjects[key];
          if (!obj) continue;

          if (obj.id === item.id) {
            const viewer = viewerRef.current;

            if (obj.dataSource) {
              const dataSource = obj.dataSource;

              if (dataSource?.entities?.values?.length > 0) {
                viewer.zoomTo(dataSource);
              }
              continue;
            }

            // --- Case: Entities array exists
            if (Array.isArray(obj.entities) && obj.entities.length > 0) {
              const firstEntity = obj.entities[0];

              // Ensure entity has a valid position
              const position = firstEntity.position?.getValue(viewer.clock.currentTime);
              if (position && !Cartesian3.equals(position, Cartesian3.ZERO)) {
                try {
                  const cartographic = Cartographic.fromCartesian(position);

                  if (!isNaN(cartographic.longitude) && !isNaN(cartographic.latitude)) {
                    const lon = CesiumMath.toDegrees(cartographic.longitude);
                    const lat = CesiumMath.toDegrees(cartographic.latitude);

                    const camera = viewer.camera;
                    const currentHeight = camera?.positionCartographic?.height;

                    if (!isNaN(lon) && !isNaN(lat) && !isNaN(currentHeight)) {
                      const destination = Cartesian3.fromDegrees(lon, lat, currentHeight);
                      camera.setView({ destination });
                    } else {
                      console.warn("Invalid lon/lat/height values:", lon, lat, currentHeight);
                    }
                  } else {
                    console.warn("Invalid cartographic coordinates from position:", position);
                  }
                } catch (e) {
                  console.error("Error converting position to cartographic:", e);
                }
              }
            }

            // --- Case: OSM data source for lanelet
            if (obj.view_type === "lanelet" && obj.osmDataSource) {
              const dataSource = obj.osmDataSource;

              if (dataSource?.entities?.values?.length > 0) {
                viewer.zoomTo(dataSource);
              }
            }
          }
        }
        break;
      }
    }
  };

  const exportAllLayers = async () => {
    if (!Array.isArray(mapObjects) || mapObjects.length === 0) {
      console.warn("⚠️ No layers found in mapObjects");
      return;
    }

    const zip = new JSZip();

    for (const obj of mapObjects) {
      if (!obj?.dataSource || !obj?.geojson || !obj?.file_name) continue;

      const content = JSON.stringify(getEditedGeoJSON(obj), null, 2);

      zip.file(removeFileExtension(obj.file_name) + obj.file_type, content);
    }

    const epoch = Math.floor(Date.now() / 1000);
    const zipName = `Export_Horizon_all_layers_${epoch}.zip`;

    const blob = await zip.generateAsync({ type: "blob" });
    saveAs(blob, zipName);
  };

  const toggleAssetView = async (item) => {
    if (!item?.osm_data) return;
    const id = item.id;

    const patch = (fn) => {
      setMapObjects((prev) => prev.map((o) => (o.id === id ? { ...o, ...fn(o) } : o)));
    };

    if (item.view_type === "line") {
      if (item.dataSource) item.dataSource.show = false;
      if (item.pointCollection) item.pointCollection.show = false;

      if (item.osmDataSource) {
        item.osmDataSource.show = true;
        patch(() => ({
          view_type: "lanelet",
          visible: true,
          is_loading: false,
          progress: null,
        }));
        return;
      }

      patch(() => ({
        is_loading: true,
        progress: null,
        view_type: "lanelet-loading",
        visible: true,
      }));

      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      await new Promise((r) => setTimeout(r, 0));

      let geoJSON;
      try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(item.osm_data, "application/xml");
        const geoJSON2 = convertOSMToGeojsonForLaneletView(xmlDoc);
        geoJSON = deduplicateLaneletFeaturesById(geoJSON2);
      } catch (err) {
        console.error("lanelet conversion error:", err);
        showToast("Lanelet conversion failed.", Type.NOTIFICATION.ERROR);
        if (item.dataSource) item.dataSource.show = true;
        if (item.pointCollection) item.pointCollection.show = true;
        patch(() => ({ is_loading: false, view_type: "line" }));
        return;
      }

      if (!geoJSON?.features?.length) {
        showToast("There is no feature.", Type.NOTIFICATION.ERROR);
        if (item.dataSource) item.dataSource.show = true;
        if (item.pointCollection) item.pointCollection.show = true;
        patch(() => ({ is_loading: false, view_type: "line" }));
        return;
      }

      addOSMGeojsonToMapForLaneletViewChunked(
        viewerRef.current,
        geoJSON,
        defaultOsmStyle,
        (entity, entityIndex, dataSource, geoJson, isNext, progress) => {
          setMapObjects((prev) =>
            prev.map((o) =>
              o.id === id
                ? {
                    ...o,
                    osmDataSource: dataSource,
                    view_type: "lanelet",
                    visible: true,
                    osmGeojson: geoJson,
                    is_loading: !!isNext,
                    progress: isNext ? progress : null,
                  }
                : o,
            ),
          );
        },
        item.file_name,
      );

      return;
    }

    // lanelet -> line
    if (item.dataSource) item.dataSource.show = true;
    if (item.pointCollection) item.pointCollection.show = true;

    if (item.osmDataSource) {
      try {
        item.osmDataSource.show = false;
      } catch (e) {
        console.warn("hide lanelet datasource failed", e);
      }
    }

    patch(() => ({
      view_type: "line",
      visible: true,
      is_loading: false,
      progress: null,
    }));
  };

  const deleteAsset = (item, forceToRemove = false) => {
    const objectToRemove = mapObjects.find((obj) => obj.id === item.id);
    setShowQueryTableDialog(false);
    if (objectToRemove) {
      if (objectToRemove.type === Type.ASSET.CZML) {
        setCzmlChart(null);
      }

      if (!forceToRemove) {
        setDeleteDialog({
          open: true,
          item: item,
        });
        return;
      } else {
        deleteAssetUtility(viewerRef.current, item, [mapObjects, setMapObjects], setImageGeojsonMode, setCamera);
      }
    }
  };

  const editLayer = (item) => {
    // Defensive checks
    if (typeof setCurrentLayerEditing !== "function" || typeof setIsOpenAddonsMenu !== "function" || typeof setEditMode !== "function") {
      console.error("Required state setters are not defined");
      return;
    }

    if (editMode) {
      // Already editing another layer → show notification
      showToast("You are currently editing a different layer", Type.NOTIFICATION.INFO);
    } else {
      // Switch to new layer
      setCurrentLayerEditing(item);
      setIsOpenAddonsMenu(false);
      setEditMode(true);
    }
  };

  const RevertToLastSaved = async (item) => {
    await loadGeojsonTypeAsync({
      geojsonData: item.last_geojson,
      id: item.id,
      fileName: item.file_name,
      viewerRef,
      setMapObjects,
      defaultGeojsonStyle,
      basicGeojsonStyle,
      defaultOsmStyle,
      setMapObjectsAssest,
      isEdited: true,
      savedStyles: selectedProject?.layers.find((x) => x.id === item.layer_id)?.style,
      zoomToDataSource: false,
    });

    setMapObjects((prev) => prev.map((x) => (x.id === item.id ? { ...x, last_geojson: null } : x)));
  };

  const toggleArrowForMapObject = (item, imageGeojson) => {
    setMapObjects((prev) =>
      prev.map((o) => {
        if (o.id !== item.id) return o;

        const hadArrow = (o.inner_entities || []).some((e) => e.type === "arrow");

        if (hadArrow) {
          const arrows = (o.inner_entities || []).find((e) => e.type === "arrow")?.entities || [];
          arrows.forEach((ent) => viewerRef.current.entities.remove(ent));

          return {
            ...o,
            inner_entities: (o.inner_entities || []).filter((e) => e.type !== "arrow"),
            arrow_visibility: false,
          };
        }

        const arrows = createArrowsBetweenPositions(viewerRef.current, imageGeojson.features);

        return {
          ...o,
          inner_entities: [...(o.inner_entities || []), { type: "arrow", entities: arrows }],
          arrow_visibility: true,
        };
      }),
    );
  };

  useEffect(() => {
    if (!["type", "name", "priority", "changed", "route"].includes(filterType)) return;

    const sorted = [...mapObjects].sort((a, b) => {
      let aValue, bValue;

      if (filterType === "name") {
        aValue = a.file_name;
        bValue = b.file_name;
      } else if (filterType === "type") {
        aValue = a.geojson_type;
        bValue = b.geojson_type;
      } else if (filterType === "changed") {
        aValue = a.is_edited ? 1 : 0;
        bValue = b.is_edited ? 1 : 0;
        return isAZ ? aValue - bValue : bValue - aValue;
      } else if (filterType === "priority") {
        aValue = Number(a.priority) ?? 0;
        bValue = Number(b.priority) ?? 0;
        return isAZ ? aValue - bValue : bValue - aValue;
      } else if (filterType === "route") {
        // helper to check route start
        const startsWithRoute = (fileName) => {
          if (!fileName) return false;
          const lower = fileName.toLowerCase();
          return lower.startsWith("route") || lower.startsWith("rt");
        };

        const aStarts = startsWithRoute(a.file_name);
        const bStarts = startsWithRoute(b.file_name);

        // if isAZ = true => route goes first
        // if isAZ = false => route goes last
        if (aStarts !== bStarts) {
          return isAZ
            ? aStarts
              ? -1
              : 1 // route first
            : aStarts
              ? 1
              : -1; // route last
        }

        // if both route or both not route, sort by name
        const compareResult = String(a.file_name).localeCompare(String(b.file_name));
        return isAZ ? compareResult : -compareResult;
      }

      if (aValue == null || bValue == null) return 0;

      const compareResult = String(aValue).localeCompare(String(bValue));
      return isAZ ? compareResult : -compareResult;
    });

    setMapObjects(sorted);
  }, [isAZ, filterType]);

  useEffect(() => {
    if (!mapObjects || typeof mapObjects !== "object") return; // prevent crash if mapObjects is null/undefined

    for (const key in mapObjects) {
      const obj = mapObjects[key];
      if (!obj) continue; // skip if object is null/undefined

      obj.visible = !isShowAllLayers; // toggle visibility flag safely

      if (typeof toggleAssetVisibility === "function") {
        toggleAssetVisibility(obj); // update asset visibility only if function exists
      } else {
        console.warn("toggleAssetVisibility is not a function:", toggleAssetVisibility);
      }
    }
  }, [isShowAllLayers]);

  const invisibleAllPolygon = () => {
    if (!mapObjects || typeof mapObjects !== "object") return; // prevent crash if mapObjects is null/undefined

    for (const key in mapObjects) {
      const obj = mapObjects[key];
      if (!obj) continue; // skip if object is null/undefined
      if (obj.geojson_type != "Polygon") continue;
      obj.visible = false; // toggle visibility flag safely

      if (typeof toggleAssetVisibility === "function") {
        toggleAssetVisibility(obj, false); // update asset visibility only if function exists
      } else {
        console.warn("toggleAssetVisibility is not a function:", toggleAssetVisibility);
      }
    }
  };

  const toggleLaneletVisibility = (item) => {
    if (!item) return;

    const viewer = viewerRef.current;
    if (!viewer) return;

    const newShow = !item.show_lanelet;
    item.show_lanelet = newShow;

    let ds = item.laneletDataSource;

    if (ds && !ds.isDestroyed?.()) {
      ds.show = newShow;
    }

    if (newShow && (!ds || ds.isDestroyed?.())) {
      const laneletDs = item.dataSource;
      if (!laneletDs) {
        setMapObjects([...mapObjects]);
        return;
      }

      ds = new CustomDataSource(`lanelet_polygons_${item.id ?? ""}`);
      viewer.dataSources.add(ds);

      const time = viewer.clock?.currentTime;

      const readProp = (prop) => {
        if (!prop) return undefined;
        try {
          return typeof prop.getValue === "function" ? prop.getValue(time) : prop;
        } catch {
          return typeof prop.getValue === "function" ? prop.getValue() : prop;
        }
      };

      // index all line entities from OTHER dataSources by properties.id
      const lineById = new Map();
      const dsCollection = viewer.dataSources;
      for (let i = 0; i < dsCollection.length; i++) {
        const otherDs = dsCollection.get(i);
        if (!otherDs || otherDs === laneletDs) continue;

        const ents = otherDs.entities?.values || [];
        for (const ent of ents) {
          if (!ent?.polyline) continue;
          const idVal = readProp(ent?.properties?.id);
          if (idVal == null) continue;
          const key = String(idVal);
          if (!lineById.has(key)) {
            lineById.set(key, ent);
          }
        }
      }

      // all lanelet entities live in laneletDs
      const centerlines = laneletDs.entities?.values || [];

      const getEndsLonLat = (entity) => {
        try {
          const posProp = entity?.polyline?.positions;
          if (!posProp) return null;
          const arr = typeof posProp.getValue === "function" ? posProp.getValue(time) : posProp;
          if (!Array.isArray(arr) || arr.length < 2) return null;

          const first = arr[0];
          const last = arr[arr.length - 1];

          const cFirst = Cartographic.fromCartesian(first);
          const cLast = Cartographic.fromCartesian(last);

          return {
            first: [CesiumMath.toDegrees(cFirst.longitude), CesiumMath.toDegrees(cFirst.latitude)],
            last: [CesiumMath.toDegrees(cLast.longitude), CesiumMath.toDegrees(cLast.latitude)],
          };
        } catch {
          return null;
        }
      };

      const isOpposite = (endRight, endLeft) => {
        const sd = (a, b) => {
          if (!a || !b) return Number.POSITIVE_INFINITY;
          const dx = (a[0] ?? 0) - (b[0] ?? 0);
          const dy = (a[1] ?? 0) - (b[1] ?? 0);
          return dx * dx + dy * dy;
        };
        const sameCost = sd(endRight.first, endLeft.first) + sd(endRight.last, endLeft.last);
        const crossCost = sd(endRight.first, endLeft.last) + sd(endRight.last, endLeft.first);
        return crossCost < sameCost;
      };

      for (const cl of centerlines) {
        if (!cl?.polyline) continue;

        const leftId = readProp(cl?.properties?.left_line_id);
        const rightId = readProp(cl?.properties?.right_line_id);
        if (leftId == null || rightId == null) continue;

        const left = lineById.get(String(leftId));
        const right = lineById.get(String(rightId));
        if (!left || !right) continue;

        const endRight = getEndsLonLat(right);
        const endLeft = getEndsLonLat(left);
        if (!endRight || !endLeft) continue;

        const opposite = isOpposite(endRight, endLeft);

        const leftUsedFirst = opposite ? endLeft.last : endLeft.first;
        const leftUsedLast = opposite ? endLeft.first : endLeft.last;

        const ring = [endRight.first, endRight.last, leftUsedLast, leftUsedFirst, endRight.first];

        const flat = [];
        for (const p of ring) {
          if (!Array.isArray(p) || p.length < 2) continue;
          flat.push(Number(p[0]), Number(p[1]));
        }
        if (flat.length < 6) continue;

        ds.entities.add({
          polygon: {
            hierarchy: Cartesian3.fromDegreesArray(flat),
            material: Color.fromCssColorString("#0000002a").withAlpha(0.3),
            outline: true,
            outlineColor: Color.fromCssColorString("#0000002a"),
          },
          properties: {
            kind: "lanelet_polygon",
            lanelet_id: readProp(cl?.properties?.id),
            left_line_id: leftId,
            right_line_id: rightId,
          },
        });
      }

      item.laneletDataSource = ds;
    }

    setMapObjects([...mapObjects]);
  };

  const createNewFile = () => {
    let geojsonData = {
      type: "FeatureCollection",
      crs: {
        type: "name",
        properties: {
          name: "urn:ogc:def:crs:OGC:1.3:CRS84",
        },
      },
      features: [],
    };
    const id = createUniqueId();
    const newMapObject = {
      id,
      name: "Geojson",
      entities: [],
      type: Type.ASSET.GEOJSON,
      file_name: "New Layer.geojson",
      file_type: ".geojson",
      inner_entities: [],
      visible: true,
      dataSource: null,
      label_visible: false,
      geojson: geojsonData,
      view_type: "line",
      osm_data: null,
      osmDataSource: null,
      style_stroke_width: null,
      style_fill_color: null,
      style_outline_color: null,
      style_type: null,
      style_pattern: "solid",
      is_center_lane: false,
      auto_select: false,
      show_lanelet: false,
      priority: 0,
      _priorityOrder: typeof performance !== "undefined" ? performance.now() : Date.now(),
      geojson_type: getGeometryTypeGeojson(geojsonData),
    };

    setMapObjects((prev) => insertByPriority(prev, newMapObject));

    loadGeoJSONToCesium({
      viewer: viewerRef.current,
      geojson: geojsonData,
      options: { ...defaultGeojsonStyle },
      callback: (dataSource, geoJson, billboards, styleFillColor, styleOutsideColor, styleStrokeWidth, styleType) => {
        setTimeout(() => {
          setMapObjects((prev) =>
            prev.map((obj) =>
              obj.id === id
                ? {
                    ...obj,
                    dataSource,
                    billboards,
                    style_stroke_width: styleStrokeWidth,
                    style_fill_color: styleFillColor,
                    style_outline_color: styleOutsideColor,
                    style_type: styleType,
                    loading: false,
                    options: { ...defaultGeojsonStyle },
                  }
                : obj,
            ),
          );
        }, 500);
      },
      geojsonType: Type.ASSET.GEOJSON,
      layerName: "New Layer.geojson",
      id: id,
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const prospectId = params.get("project_id");

    if (prospectId && projects.length > 0) {
      const found = projects.find((p) => String(p.id) === String(prospectId));
      if (found) {
        setSelectedProject(found);
      } else {
        showToast("You don't have access to this project", Type.NOTIFICATION.ERROR);
      }
    }
  }, [projects]);

  const reloadLayer = (item) => {
    const formData = new FormData();
    formData.append("layer_id", item.layer_id);

    serviceGetLayerFromServer(formData)
      .then((res) => {
        if (res.data.status == "success") {
          fetch("https://matrix.gatik.ai" + res.data.layers[0].file_url)
            .then((response) => {
              if (!response.ok) {
              }
              return response.json();
            })
            .then(async (data) => {
              try {
                viewerRef.current.dataSources.remove(item.dataSource);

                const newDs = await loadGeoJSONToCesium({
                  viewer: viewerRef.current,
                  geojson: data,
                  options: { ...item.options, zoomToDataSource: false },
                  geojsonType: item.type,
                  layerName: item.file_name,
                  id: item.id,
                });

                item.dataSource = newDs;
                item.is_edited = false;

                setMapObjects((prev) => prev.map((o) => (o.id === item.id ? { ...o, dataSource: newDs, is_edited: false } : o)));

                showToast("GeoJSON updated successfully", Type.NOTIFICATION.SUCCESS);
              } catch (err) {
                console.error("Failed to reload GeoJSON:", err);
                showToast("Failed to update GeoJSON layer", Type.NOTIFICATION.ERROR);
              }
            })
            .catch((error) => {
              console.error(error);
            });
        }
      })
      .catch((err) => {
        showToast(err.message, Type.NOTIFICATION.ERROR);
        console.error("Error", err);
      });
  };

  const [scanOn, setScanOn] = useState(false);
  const scanCtrlRef = useRef(null);
  function pickDataSources() {
    let laneletDS = null;
    let virtualDS = null;
    let markerDS = null;
    let turningDS = null;
    let turningArcDS = null;

    for (const key in mapObjects) {
      const mo = mapObjects[key];

      if (mo?.dataSource && /lanelet/i.test(mo.file_name || "")) laneletDS = mo.dataSource;
      if (
        mo?.dataSource &&
        (mo.file_name || "")
          .replace(/[\s_-]/g, "")
          .toLowerCase()
          .includes("virtualline")
      ) {
        virtualDS = mo.dataSource;
      }

      if (
        mo?.dataSource &&
        (mo.file_name || "")
          .replace(/[\s_-]/g, "")
          .toLowerCase()
          .includes("lanemarker")
      ) {
        markerDS = mo.dataSource;
      }
      if (
        mo?.dataSource &&
        (mo.file_name || "")
          .replace(/[\s_-]/g, "")
          .toLowerCase()
          .includes("turningline")
      ) {
        turningDS = mo.dataSource;
      }
      if (
        mo?.dataSource &&
        (mo.file_name || "")
          .replace(/[\s_-]/g, "")
          .toLowerCase()
          .includes("turningarc")
      ) {
        turningArcDS = mo.dataSource;
      }
    }
    return { laneletDS, virtualDS, markerDS, turningDS, turningArcDS };
  }
  async function toggleScanRoute(item) {
    const next = !scanOn;
    setScanOn(next);

    if (next) {
      const routeDS = item.dataSource;
      const { laneletDS, virtualDS, markerDS, turningDS, turningArcDS } = pickDataSources(item);

      if (!viewerRef?.current) {
        showToast("Viewer is not ready. Load the map first.", Type.NOTIFICATION.WARNING);
        return;
      }
      if (!routeDS && !laneletDS) {
        showToast("Route and Lanelet data sources are missing. Load them first.", Type.NOTIFICATION.WARNING);
        return;
      }
      if (!routeDS) {
        showToast("Route data source is missing. Load or create it.", Type.NOTIFICATION.WARNING);
        return;
      }
      if (!laneletDS) {
        showToast("Lanelet data source is missing. Load it first.", Type.NOTIFICATION.WARNING);
        return;
      }
      if (laneletDS?.show === false) {
        showToast("Lanelet data source is hidden. Make it visible.", Type.NOTIFICATION.WARNING);
        return;
      }

      try {
        scanCtrlRef.current?.clear?.();

        scanCtrlRef.current = await generateAutoLaneletRoute({
          viewer: viewerRef.current,
          routeDS,
          laneletDS,
          virtualDS,
          markerDS,
          turningDS,
          turningArcDS,
          radiusStart: 3,
          radiusHit: 3,
          widthGreen: 10,
          verbose: false,
          onDone: (res, geojson) => {
            setLayersApprovalScanRouteDialog({
              geojson: geojson.lanelet_points_geojson,
              resetStyles: () => {
                scanCtrlRef.current?.clear?.();
                scanCtrlRef.current = null;

                const v = viewerRef.current;
                if (v?.scene?.requestRenderMode) v.scene.requestRender();
              },
            });
            setScanOn(false);
          },
          onAbort: (reason, geojson) => {
            setLayersApprovalScanRouteDialog({
              geojson: geojson.lanelet_points_geojson,
              resetStyles: () => {
                scanCtrlRef.current?.clear?.();
                scanCtrlRef.current = null;

                const v = viewerRef.current;
                if (v?.scene?.requestRenderMode) v.scene.requestRender();
              },
            });
            showToast(reason, Type.NOTIFICATION.WARNING, { duration: 15000, closable: true });
            setScanOn(false);
          },
        });
      } catch (e) {
        setScanOn(false);
      }
    } else {
      scanCtrlRef.current?.clear?.();
      scanCtrlRef.current = null;

      const v = viewerRef.current;
      if (v?.scene?.requestRenderMode) v.scene.requestRender();
    }
  }

  const addToCurrentProject = async (item, project) => {
    const projectId = project.id;

    let listOfLayersId = [];
    for (const key in project.layers) {
      if (project.layers[key]?.id) {
        listOfLayersId.push(project.layers[key]?.id);
      }
    }

    const formData = new FormData();

    const geojson = item.type == Type.ASSET.GEOJSON ? getEditedGeoJSON(item) : item.geojson;

    let file = createGeoJSONFile(geojson, removeEpochSuffix(removeFileExtension(item.file_name)));
    let bbox = bboxCornersLonLatArrays(geojson);

    if (!item.is_load_from_database) {
      formData.append("file", file);
    }

    formData.append(
      "data",
      JSON.stringify({
        layer_name: removeFileExtension(file.name),
        layer_style: {
          style_stroke_width: item.style_stroke_width ? item.style_stroke_width : null,
          style_fill_color: item.style_fill_color ? item.style_fill_color : null,
          style_outline_color: item.style_outline_color ? item.style_outline_color : null,
          style_type: item.style_type ? item.style_type.toLowerCase() : "solid",
          style_pattern: item.style_pattern ? item.style_pattern : "solid",
          style_fill_pattern: item.style_fill_pattern ? item.style_fill_pattern : "",
          style_invisible_default: item.style_invisible_default ? item.style_invisible_default : false,
          label_visible: item.label_visible ? item.label_visible : null,
          label_text_size: item.label_text_size ? item.label_text_size : null,
          label_offset: item.label_offset ? item.label_offset : null,
          label_text_color: item.label_text_color ? item.label_text_color : null,
          label_interval: item.label_interval ? item.label_interval : null,
          selected_tags_for_label: item.selected_tags_for_label ? item.selected_tags_for_label : null,
          db_schema: item.sql_schema,
          db_table: item.sql_table,
          db_geometry_field: item.sql_geometry_field,
          db_db: item.sql_db,
          db_where: item.sql_where,
          is_disable_loading: item.isDisableLoading,
        },
        layer_type: item.is_load_from_database ? "postgis" : item.type == Type.ASSET.OSM ? "osm" : item.type == Type.ASSET.GEOJSON ? "geojson" : "camera",
        geometry_type: getGeometryTypeGeojson(geojson) || "mixed",
        description: "",
        username: userData.user.username,
        bbox: JSON.stringify(bbox),
      }),
    );

    if (item.layer_id) {
      formData.append("layer_id", item.layer_id);
    }

    await serviceSaveLayer(formData)
      .then((res) => {
        if (res.data.status == "failed") {
          showToast(res.data.message, Type.NOTIFICATION.WARNING);
        } else {
          showToast("Stored " + removeEpochSuffix(removeFileExtension(res.data.layer_name)), Type.NOTIFICATION.SUCCESS);
          listOfLayersId.push(res.data.layer_id);
          item.file_name = removeFileExtension(res.data.layer_name) + item.file_type;
          item.is_edited = false;
          item.project_id = projectId;
          item.layer_id = res.data.layer_id;
        }
      })
      .catch((err) => {
        console.error("Error", err);
      });

    const formData2 = new FormData();
    formData2.append(
      "data",
      JSON.stringify({
        username: userData.user.username,
        layers: [...new Set(listOfLayersId)],
      }),
    );
    formData2.append("project_id", projectId);

    serviceCreateOrEditProjects(formData2, userData.token)
      .then((res) => {
        if (res.data.status == "success") {
          showToast(res.data.message, Type.NOTIFICATION.SUCCESS);
          getProjects({
            userData,
            setProjects,
          });
        }
      })
      .catch((err) => {
        showToast(err.message, Type.NOTIFICATION.WARNING);
        console.error("Error", err);
      });
  };

  const toggleAssetVisibility = createToggleAssetVisibility({
    viewerRef,
    setMapObjects,

    defaultGeojsonStyle,
    basicGeojsonStyle,
    defaultOsmStyle,
    setMapObjectsAssest,
    selectedProject,

    toggleLabelVisibility,
    toggleDirectionalArrows,
    directionArrowSettingRef,

    scanOn,
    toggleScanRoute,
  });

  return (
    <>
      <VStack width="full">
        <LayerImportRow
          uploadKey={uploadKey}
          setUploadKey={setUploadKey}
          selectedImportType={selectedImportType}
          setSelectedImportType={setSelectedImportType}
          importFromUrl={importFromUrl}
          createNewFile={createNewFile}
          cleanMap={cleanMap}
          exportAllLayers={exportAllLayers}
        />

        <LayerSortAndSearchBar
          filterType={filterType}
          setFilterType={setFilterType}
          layerSearch={layerSearch}
          setLayerSearch={setLayerSearch}
          isAZ={isAZ}
          setIsAZ={setIsAZ}
          isShowAllLayers={isShowAllLayers}
          setIsShowAllLayers={setIsShowAllLayers}
          invisibleAllPolygon={invisibleAllPolygon}
          selectedObjectsForDeleteState={[selectedObjectsForDelete, setSelectedObjectsForDelete]}
        />
      </VStack>

      {loadingDataForLayers.current?.show && (
        <HStack width={"full"} pr={3}>
          <Progress.Root width="full" striped animated value={(loadingDataForLayers.current?.current * 100) / loadingDataForLayers.current?.total}>
            <Progress.Track>
              <Progress.Range />
            </Progress.Track>
          </Progress.Root>
          <Text>
            {loadingDataForLayers.current?.current}/{loadingDataForLayers.current?.total}
          </Text>
        </HStack>
      )}

      <Box flex="1" minH={0} mt={0} gap={3} h="100%" overflowY="scroll">
        {Object.entries(
          mapObjects
            .sort((a, b) => String(a.project_id ?? "").localeCompare(String(b.project_id ?? "")))
            .reduce((acc, item) => {
              const pid = String(item.project_id ?? "NoProject");
              if (!acc[pid]) acc[pid] = [];
              acc[pid].push(item);
              return acc;
            }, {}),
        ).map(([projectId, layers]) => (
          <Box key={projectId} border="1px solid #ccc" borderRadius="md" p={2} mt={2}>
            <Text fontWeight="bold" fontSize="sm" mb={1}>
              Project: {projects.find((p) => String(p.id) === String(projectId))?.project_name || `Unknown (${projectId})`}
            </Text>

            {layers
              .filter((item) => !layerSearch || item.file_name.toLowerCase().includes(layerSearch.toLowerCase()))
              .map((item, index) => (
                <Card.Root
                  mb={2}
                  key={index}
                  size="sm"
                  bg={
                    activeLayer == item?.id ? "green.100" : !item.visible ? "blackAlpha.400" : editMode && item?.id == currentLayerEditing?.id ? "green.300" : item?.is_edited ? "orange.100" : "white"
                  }
                  cursor="pointer"
                  onClick={() => setActiveLayer(item.id)}
                >
                  <Card.Header py={0} pt={1} pr={2}>
                    <HStack justify={"space-between"}>
                      <HStack>
                        <Checkbox.Root
                          size="sm"
                          checked={selectedObjectsForDelete.includes(item.id)}
                          onCheckedChange={(e) => {
                            setSelectedObjectsForDelete((prev) => {
                              if (e.checked === true) {
                                return prev.includes(item.id) ? prev : [...prev, item.id];
                              }
                              return prev.filter((x) => x !== item.id);
                            });
                          }}
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                        </Checkbox.Root>

                        {editMode && currentLayerEditing?.id === item.id && <LuSettings />}
                        <Editable.Root value={removeFileExtension(item?.file_name)} activationMode="dblclick" width={"fit-content"} fontWeight={"medium"}>
                          <Editable.Preview
                            maxWidth={"220px"}
                            style={{
                              whiteSpace: "pre-wrap",
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                              textAlign: "start",
                            }}
                          />
                          <Editable.Input
                            data-allow-hotkeys="Enter"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.stopPropagation();
                                e.currentTarget.blur();
                              }
                            }}
                            maxWidth={"220px"}
                            onBlur={(v) => {
                              let tempMapObjects = [...mapObjects];
                              for (const key in tempMapObjects) {
                                if (tempMapObjects[key].id == item.id) {
                                  tempMapObjects[key].file_name = v.target.value + tempMapObjects[key].file_type;
                                  tempMapObjects[key].dataSource.layerName = v.target.value + tempMapObjects[key].file_type;
                                  tempMapObjects[key].is_edited = true;

                                  break;
                                }
                              }
                              setMapObjects(tempMapObjects);
                            }}
                          />
                        </Editable.Root>

                        <Clipboard.Root
                          value={removeFileExtension(item.file_name)}
                          onClick={() => {
                            showToast("Copied", Type.NOTIFICATION.SUCCESS);
                          }}
                        >
                          <Clipboard.Trigger asChild>
                            <IconButton variant="surface" size="xs">
                              <Clipboard.Indicator />
                            </IconButton>
                          </Clipboard.Trigger>
                        </Clipboard.Root>
                      </HStack>

                      <VStack gap={0}>
                        <HStack width={"full"} justify={"end"} gap={2} pt={2} pr={2}>
                          {(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN)) &&
                          item.type != Type.ASSET.THREEDTILES &&
                          item?.is_load_from_database &&
                          item?.is_edited ? (
                            <Button
                              display={"contents"}
                              width={"20px"}
                              size="md"
                              px={0}
                              variant="none"
                              onClick={() => {
                                saveToDatabse(item);
                              }}
                            >
                              <Tooltip content={"Save To Database " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.SAVE_TO_DATABASE)}>
                                <LuDatabaseBackup />
                              </Tooltip>
                            </Button>
                          ) : null}
                          {(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN)) && item.type != Type.ASSET.CZML ? (
                            <Button
                              display={"contents"}
                              width={"20px"}
                              size="md"
                              px={0}
                              variant="none"
                              onClick={(e) => {
                                if (e.shiftKey) {
                                  setCurrentLayer(item);
                                  setShowLayersSaveDialog(true);
                                  setAutoLayersSave(true);
                                } else {
                                  setCurrentLayer(item);
                                  if (item.type != Type.ASSET.IMAGERY) {
                                    let changeset = diffGeoJSON(item.original_geojson, getEditedGeoJSON(item));

                                    if (changeset.length == 0) {
                                      setAutoLayersSave(true);
                                    }
                                  }

                                  setShowLayersSaveDialog(true);
                                }
                              }}
                            >
                              <Tooltip content={"Save To Server " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.SAVE_LAYER_TO_SERVER)}>
                                <LuSave />
                              </Tooltip>
                            </Button>
                          ) : null}

                          {item.type != Type.ASSET.THREEDTILES && item.type != Type.ASSET.CZML && item.type != Type.ASSET.IMAGERY ? (
                            <Button
                              size="md"
                              px={0}
                              variant="none"
                              display={"contents"}
                              onClick={() => {
                                switch (item.type) {
                                  case Type.ASSET.OSM:
                                  case Type.ASSET.GEOJSON_IMAGE:
                                  case Type.ASSET.GEOJSON:
                                    setExportDialog(true);
                                    setCurrentLayer(item);
                                    break;
                                  case Type.ASSET.CSV:
                                    if (item.dataSource) {
                                      setExportDialog(true);
                                      setCurrentLayer(item);
                                    }
                                    break;
                                }
                              }}
                            >
                              <Tooltip content="Export">
                                <LuUpload />
                              </Tooltip>
                            </Button>
                          ) : null}

                          {item.type == Type.ASSET.CZML ? (
                            <Button
                              size="md"
                              px={0}
                              variant="none"
                              display={"contents"}
                              onClick={() => {
                                let newVal = czmlChart ? null : item;
                                setCzmlChart(newVal);
                              }}
                            >
                              <Tooltip content="Chart">
                                <LuChartArea color={czmlChart ? "green" : "red"} />
                              </Tooltip>
                            </Button>
                          ) : null}

                          <Button
                            display={"contents"}
                            size="md"
                            variant="none"
                            px={0}
                            onClick={() => {
                              toggleAssetVisibility(item);
                            }}
                          >
                            <Tooltip content={"Toggle Visibility " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.TOGGLE_LAYER_VISIBILITY)}>{item.visible ? <LuEye /> : <LuEyeOff />}</Tooltip>
                          </Button>
                        </HStack>
                        <HStack width={"full"} justify={"end"} gap={2} pt={2} pr={2}>
                          {item.type != Type.ASSET.GEOJSON_IMAGE &&
                            !item?.isOverlayText &&
                            item.type != Type.ASSET.THREEDTILES &&
                            item.type != Type.ASSET.OSM &&
                            item.type != Type.ASSET.CSV &&
                            item.type != Type.ASSET.IMAGERY &&
                            item.type != Type.ASSET.CZML && (
                              <Button
                                size="md"
                                px={0}
                                color={item.arrow_visibility ? "green" : "MenuText"}
                                variant="none"
                                display={"contents"}
                                onClick={() => {
                                  toggleDirectionalArrows(viewerRef.current, item.dataSource, !item.arrow_visibility, directionArrowSettingRef);
                                  setMapObjects((prev) => prev.map((o) => (o.id === item.id ? { ...o, arrow_visibility: !o.arrow_visibility } : o)));
                                }}
                              >
                                <Tooltip content={"Toggle Direction Visibility " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.TOGGLE_DIRECTION_VISIBILITY)}>
                                  <LuMousePointer2 />
                                </Tooltip>
                              </Button>
                            )}

                          {item.type != Type.ASSET.IMAGERY && (
                            <Button
                              display={"contents"}
                              size="md"
                              variant="none"
                              px={0}
                              onClick={() => {
                                zoomToExtend(item);
                              }}
                            >
                              <Tooltip content={"Zoom To Extend " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.ZOOM_TO_EXTENT)}>
                                <LuZoomIn />
                              </Tooltip>
                            </Button>
                          )}
                        </HStack>
                      </VStack>
                    </HStack>
                    {item.isDisableLoading && <Text>Default Loading: Disabled</Text>}
                  </Card.Header>
                  <Card.Body color="fg.muted" py={0} pr={0}>
                    <HStack width={"full"} justify={"space-between"}>
                      <HStack>
                        {item.dataSource && item.dataSource.entities.values.length}

                        {["Point", "MultiPoint"].includes(getGeometryTypeGeojson(item.geojson)) && <LuCircle />}
                        {["LineString", "MultiLineString"].includes(getGeometryTypeGeojson(item.geojson)) && <LuSpline />}
                        {["Polygon", "MultiPolygon"].includes(getGeometryTypeGeojson(item.geojson)) && <LuDiamond />}
                        {item.type == Type.ASSET.THREEDTILES && <LuBox />}
                        {item.style_outline_color && (
                          <ColorSwatch
                            value={item.style_outline_color}
                            onClick={() => {
                              if (item.dataSource) {
                                setShowStyleDialog(true);
                                setCurrentLayer(item);
                              }
                            }}
                          />
                        )}

                        <Text fontSize={"12px"} lineClamp={1}>
                          {item.is_load_from_database ? "PGSQL" : item.type == Type.ASSET.THREEDTILES ? "3D Tiles" : item.type}
                        </Text>
                        <Text fontSize={"12px"} color={"orange"}>
                          {item?.is_edited ? "(Edited)" : null}
                        </Text>
                      </HStack>
                      <HStack gap={0}>
                        {item.url && (
                          <Button
                            size="md"
                            p={0}
                            variant="none"
                            onClick={() => {
                              deleteAsset(item, true);
                              importFromUrl(item.url, item.type);
                            }}
                          >
                            <LuRefreshCcw />
                          </Button>
                        )}

                        <Button
                          width={"20px"}
                          size="md"
                          px={0}
                          color={"red.500"}
                          variant="none"
                          onClick={() => {
                            deleteAsset(item);
                          }}
                        >
                          <Tooltip content={"Remove Layer " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.DELETE_LAYER)}>
                            <LuTrash />
                          </Tooltip>
                        </Button>

                        {item.type !== Type.ASSET.IMAGERY && (
                          <Box position="relative" display="inline-flex">
                            <Button
                              size="md"
                              p={0}
                              color={"MenuText"}
                              variant="none"
                              onClick={() => {
                                setCurrentLayer(item);
                                setFilterGeojsonDialog(true);
                              }}
                            >
                              <Tooltip content="Filter">
                                <LuFilter />
                              </Tooltip>
                            </Button>

                            {item?.filters?.length > 0 && (
                              <Box position="absolute" top="10px" right="10px" w="8px" h="8px" borderRadius="full" bg="red.500" border="2px solid" borderColor="red.500" pointerEvents="none" />
                            )}
                          </Box>
                        )}

                        {item.type != Type.ASSET.THREEDTILES && !item?.isOverlayText && item.type != Type.ASSET.CZML && item.type != Type.ASSET.IMAGERY && (
                          <Menu.Root>
                            <Menu.Trigger asChild>
                              <Button size="md" color={"MenuText"} pl={0} pr={2} variant="none">
                                <Tooltip content="More ...">
                                  <LuSettings />
                                </Tooltip>
                              </Button>
                            </Menu.Trigger>

                            <Menu.Positioner>
                              <Menu.Content>
                                {item.type == Type.ASSET.OSM && item.osm_data && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="ToggleAssetView"
                                    onClick={() => {
                                      toggleAssetView(item);
                                    }}
                                  >
                                    <Box flex="1">{item.view_type == "line" ? "Lanelet View" : "Line View"}</Box>
                                    {item.is_loading ? <Spinner size={"sm"} /> : <LuRectangleHorizontal />}
                                  </Menu.Item>
                                )}

                                {item.type == Type.ASSET.OSM && item.osm_data && item.view_type != "line" && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="RightLaneVisibility"
                                    color={item.right_lane_visibility ? "green" : "orange"}
                                    onClick={() => {
                                      toggleRightLaneHighlight(item.osmDataSource);
                                      setMapObjects((prev) => prev.map((o) => (o.id === item.id ? { ...o, right_lane_visibility: !o.right_lane_visibility } : o)));
                                    }}
                                  >
                                    <Box flex="1">Right Lane Visibility [{item.right_lane_visibility ? "Visible" : "Hidden"}]</Box>
                                    {item.is_loading ? <Spinner size={"sm"} /> : <LuRectangleHorizontal />}
                                  </Menu.Item>
                                )}

                                {item.is_center_lane && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="RightLaneVisibility2"
                                    onClick={() => {
                                      toggleRightLaneBoundaryHighlight(viewerRef.current, item.dataSource);
                                    }}
                                  >
                                    <Box flex="1">Right Lane Visibility </Box>
                                    {item.is_loading ? <Spinner size={"sm"} /> : <LuRectangleHorizontal />}
                                  </Menu.Item>
                                )}

                                {/* {item.type == Type.ASSET.OSM && item.osm_data && item?.view_type == "lanelet" && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="CpmpareWith"
                                    onClick={() => {
                                      setCurrentLayer(item);
                                      setShowCompareDialog(true);
                                    }}
                                  >
                                    <Box flex="1">Highlight Route</Box>
                                    <LuCheck />
                                  </Menu.Item>
                                )} */}

                                {item.type == Type.ASSET.GEOJSON && removeFileExtension(item.file_name).toLowerCase().includes("lanelet") && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="CpmpareWith"
                                    onClick={() => {
                                      setCurrentLayer(item);
                                      setShowCompareDialog(true);
                                    }}
                                  >
                                    <Box flex="1">Highlight Route</Box>
                                    <LuCheck />
                                  </Menu.Item>
                                )}

                                {removeFileExtension(item.file_name).toLowerCase().includes("lanelet") && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="ShowLanelet"
                                    color={item.show_lanelet ? "green" : "orange"}
                                    onClick={() => {
                                      toggleLaneletVisibility(item);
                                    }}
                                  >
                                    <Box flex="1">Show Lanelet [{item.show_lanelet ? "Visible" : "Hidden"}]</Box>
                                    <LuCircle />
                                  </Menu.Item>
                                )}

                                {item.type == Type.ASSET.OSM && item.view_type == "lanelet" && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="Filter"
                                    onClick={() => {
                                      setCurrentLayer(item);
                                      setFilterDialog(true);
                                    }}
                                  >
                                    <Box flex="1">Filter</Box>
                                    <LuFilter />
                                  </Menu.Item>
                                )}

                                {(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.DIGITIZER) || userType.includes(Type.ROLE.SUPER_ADMIN)) &&
                                  item.type != Type.ASSET.GEOJSON_IMAGE &&
                                  item.type != Type.ASSET.THREEDTILES &&
                                  item.type != Type.ASSET.CSV && (
                                    <Menu.Item
                                      cursor={"pointer"}
                                      value="EditLayer"
                                      onClick={() => {
                                        editLayer(item);
                                      }}
                                    >
                                      <Box flex="1"> Edit Layer {getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.ACTIVE_EDIT_LAYER)}</Box>
                                      <LuPencilRuler />
                                    </Menu.Item>
                                  )}

                                {(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.DIGITIZER) || userType.includes(Type.ROLE.SUPER_ADMIN)) &&
                                  item.type == Type.ASSET.GEOJSON &&
                                  item.last_geojson && (
                                    <Menu.Item
                                      cursor={"pointer"}
                                      value="RevertToLastSaved"
                                      onClick={() => {
                                        RevertToLastSaved(item);
                                      }}
                                    >
                                      <Box flex="1"> Revert to last saved </Box>
                                      <LuUndo />
                                    </Menu.Item>
                                  )}

                                {(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.DIGITIZER) || userType.includes(Type.ROLE.SUPER_ADMIN)) &&
                                  item.type != Type.ASSET.GEOJSON_IMAGE &&
                                  item.type != Type.ASSET.THREEDTILES &&
                                  item.type != Type.ASSET.CSV && (
                                    <Menu.Item
                                      cursor={"pointer"}
                                      value="JsonEditor"
                                      onClick={() => {
                                        setShowJsonEditor(true);
                                        setCurrentLayerForJsonEditor(item);
                                      }}
                                    >
                                      <Box flex="1"> JSON Editor</Box>
                                      <LuFileJson2 />
                                    </Menu.Item>
                                  )}

                                {["LineString", "MultiLineString"].includes(getGeometryTypeGeojson(item.geojson)) && item.type == Type.ASSET.GEOJSON && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="TotalLength"
                                    onClick={() => {
                                      let total = getDataSourcePolylineMiles(item.dataSource);
                                      showToast(`Total Length: ${total.toFixed(2)} miles`, Type.NOTIFICATION.INFO);
                                    }}
                                  >
                                    <Box flex="1"> Total Length In Miles</Box>
                                    <LuFileJson2 />
                                  </Menu.Item>
                                )}

                                {item?.layer_id && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="Reload"
                                    onClick={() => {
                                      reloadLayer(item);
                                    }}
                                  >
                                    <Box flex="1"> Reload</Box>
                                    <LuRefreshCcw />
                                  </Menu.Item>
                                )}
                                {item.is_load_from_database && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="NewPGSQLInput"
                                    onClick={() => {
                                      setShowChangeSQLDialog(item);
                                    }}
                                  >
                                    <Box flex="1"> Change Query</Box>
                                    <LuDatabaseBackup />
                                  </Menu.Item>
                                )}

                                {item.is_load_from_database && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="ConvertToGeojson"
                                    onClick={() => {
                                      setMapObjects((prev) => prev.map((o) => (o.id === item.id ? { ...o, type: Type.ASSET.GEOJSON, is_edited: true, is_load_from_database: false } : o)));
                                    }}
                                  >
                                    <Box flex="1"> Convert To Geojson Type</Box>
                                    <LuArrowRightLeft />
                                  </Menu.Item>
                                )}

                                <Menu.Item
                                  cursor={"pointer"}
                                  value="SwitchLayer"
                                  onClick={() => {
                                    setCurrentLayer(item);
                                    setShowSwitchLayerDialog(true);
                                  }}
                                >
                                  <Box flex="1"> Switch Layer</Box>
                                  <LuArrowLeftRight />
                                </Menu.Item>
                                {(item?.file_name.toLowerCase().startsWith("route") || item?.file_name.toLowerCase().startsWith("rt")) && (
                                  <Menu.Item
                                    onClick={() => {
                                      if (item.visible) {
                                        toggleScanRoute(item);
                                      }
                                    }}
                                    color={scanOn ? "green" : "red"}
                                    value="GenerateRoute"
                                    cursor="pointer"
                                  >
                                    <Box flex="1">{scanOn ? "Scan Route… (click to stop)" : "Scan Route"}</Box>

                                    <LuScanLine />
                                  </Menu.Item>
                                )}

                                {(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN)) &&
                                  item.type != Type.ASSET.GEOJSON_IMAGE &&
                                  item.type != Type.ASSET.THREEDTILES &&
                                  item.type != Type.ASSET.CSV && (
                                    <Menu.Item
                                      cursor={"pointer"}
                                      value="Duplicate"
                                      onClick={() => {
                                        loadGeojsonTypeAsync({
                                          originalCameraControls,
                                          geojsonData: getEditedGeoJSON(item),
                                          fileName: item.file_name,
                                          viewerRef,
                                          setMapObjects,
                                          defaultGeojsonStyle,
                                          basicGeojsonStyle,
                                          defaultOsmStyle,
                                        });

                                        showToast("Duplicated", Type.NOTIFICATION.SUCCESS);
                                      }}
                                    >
                                      <Box flex="1">Duplicate</Box>
                                      <LuBookCopy />
                                    </Menu.Item>
                                  )}

                                {(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN)) &&
                                  item.type == Type.ASSET.GEOJSON &&
                                  selectedProject &&
                                  !item?.project_id && (
                                    <Menu.Item
                                      cursor={"pointer"}
                                      value="AddToCurrentProject"
                                      onClick={() => {
                                        addToCurrentProject(item, selectedProject);
                                      }}
                                    >
                                      <Box flex="1">Add To Current Project</Box>
                                      <LuFolderSymlink />
                                    </Menu.Item>
                                  )}

                                {(matchKeyInText(item.file_name, Type.LAYER.LANELET) ||
                                  matchKeyInText(item.file_name, Type.LAYER.LANE_MARKER) ||
                                  matchKeyInText(item.file_name, Type.LAYER.VIRTUAL_LINE) ||
                                  matchKeyInText(item.file_name, Type.LAYER.CURBSTONE) ||
                                  matchKeyInText(item.file_name, Type.LAYER.ROAD_EDGE)) && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="RoadSlope"
                                    onClick={() => {
                                      setShowRoadSlopeDialog({
                                        open: true,
                                        data: item,
                                      });
                                    }}
                                  >
                                    <Box flex="1">Road Slope</Box>
                                    <LuTriangleRight />
                                  </Menu.Item>
                                )}

                                {(matchKeyInText(item.file_name, Type.LAYER.LANELET) ||
                                  matchKeyInText(item.file_name, Type.LAYER.LANE_MARKER) ||
                                  matchKeyInText(item.file_name, Type.LAYER.VIRTUAL_LINE) ||
                                  matchKeyInText(item.file_name, Type.LAYER.CURBSTONE) ||
                                  matchKeyInText(item.file_name, Type.LAYER.ROAD_EDGE)) && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="FindBreakPoints"
                                    onClick={() => {
                                      setShowZigzagDialog({
                                        open: true,
                                        data: item,
                                      });
                                    }}
                                  >
                                    <Box flex="1">Find Break Points</Box>
                                    <LuSeparatorVertical />
                                  </Menu.Item>
                                )}

                                {(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN)) &&
                                  item.type != Type.ASSET.GEOJSON_IMAGE &&
                                  item.type != Type.ASSET.THREEDTILES &&
                                  item.type != Type.ASSET.CSV && (
                                    <Menu.Item
                                      cursor={"pointer"}
                                      value="Simplify"
                                      onClick={() => {
                                        simplifyCesiumDataSource(item.dataSource, parseInt(simplifyToleranceSetting.distance));
                                        setMapObjects((prev) => prev.map((o) => (o.id === item.id ? { ...o, is_edited: true } : o)));

                                        showToast("Simplified", Type.NOTIFICATION.SUCCESS);
                                      }}
                                    >
                                      <Box flex="1">Simplify</Box>
                                      <LuMapPinMinus />
                                    </Menu.Item>
                                  )}

                                {(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN)) &&
                                  item.type != Type.ASSET.GEOJSON_IMAGE &&
                                  item.type != Type.ASSET.THREEDTILES &&
                                  item.type != Type.ASSET.CSV && (
                                    <Menu.Item
                                      cursor={"pointer"}
                                      value="GeneratePoints"
                                      onClick={() => {
                                        generatePointsCesiumDataSource(item.dataSource, parseInt(simplifyToleranceSetting?.generate_distance));
                                        setMapObjects((prev) => prev.map((o) => (o.id === item.id ? { ...o, is_edited: true } : o)));

                                        showToast("Points generated", Type.NOTIFICATION.SUCCESS);
                                      }}
                                    >
                                      <Box flex="1">Generate Points</Box>
                                      <LuMapPinPlus />
                                    </Menu.Item>
                                  )}

                                {(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN)) &&
                                  item.type == Type.ASSET.GEOJSON &&
                                  getDataSourceGeometryType(item.dataSource) == "line" && (
                                    <Menu.Item
                                      cursor={"pointer"}
                                      value="FindClosePoints"
                                      color={item?.FindClosePoints ? "green" : "red"}
                                      onClick={() => {
                                        runFindClosePoints(item.dataSource, viewerRef.current, !item.FindClosePoints, {
                                          distanceThresholdMeters: simplifyToleranceSetting?.find_close_point,
                                        });
                                        setMapObjects((prev) => prev.map((o) => (o.id === item.id ? { ...o, FindClosePoints: !item.FindClosePoints } : o)));
                                      }}
                                    >
                                      <Box flex="1">Find Close Points</Box>
                                      <LuMapPinX />
                                    </Menu.Item>
                                  )}

                                <Menu.Item
                                  cursor={"pointer"}
                                  value="ShowLastId"
                                  onClick={async () => {
                                    const ds = item?.dataSource;
                                    const entities = ds?.entities?.values || [];
                                    const viewer = viewerRef.current;
                                    const time = viewer?.clock?.currentTime;

                                    let maxId = -Infinity;

                                    for (const entity of entities) {
                                      const props = entity?.properties?.getValue?.(time) || entity?.properties?.getValue?.() || entity?.properties;
                                      const raw = props?.id;

                                      let v;
                                      try {
                                        v = typeof raw?.getValue === "function" ? raw.getValue(time) : raw;
                                      } catch {
                                        try {
                                          v = typeof raw?.getValue === "function" ? raw.getValue() : raw;
                                        } catch {
                                          v = raw;
                                        }
                                      }

                                      const n = Number(v);
                                      if (Number.isFinite(n)) maxId = Math.max(maxId, n);
                                    }

                                    if (!Number.isFinite(maxId)) {
                                      showToast("No valid id found", Type.NOTIFICATION.WARNING);
                                      return;
                                    }

                                    const nextId = maxId + 2;

                                    showToast(String(nextId), Type.NOTIFICATION.SUCCESS);

                                    try {
                                      await navigator.clipboard.writeText(String(nextId));
                                    } catch {}
                                  }}
                                >
                                  <Box flex="1">Show Last ID</Box>
                                  <LuHash />
                                </Menu.Item>

                                {(userType.includes(Type.ROLE.ANNOTATOR) || userType.includes(Type.ROLE.ADMIN) || userType.includes(Type.ROLE.SUPER_ADMIN)) &&
                                  item.type != Type.ASSET.GEOJSON_IMAGE &&
                                  item.type != Type.ASSET.THREEDTILES &&
                                  item.type != Type.ASSET.CSV &&
                                  item.layer_id && (
                                    <Menu.Item
                                      cursor={"pointer"}
                                      value="CopyToProject"
                                      onClick={() => {
                                        setCurrentLayer(item);
                                        setIsShowCopyToProjectDialog(true);
                                      }}
                                    >
                                      <Box flex="1">Copy To Project</Box>
                                      <LuCopy />
                                    </Menu.Item>
                                  )}

                                {item.type !== Type.ASSET.GEOJSON_IMAGE && item.type !== Type.ASSET.OSM && item.type != Type.ASSET.THREEDTILES && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="QueryTable"
                                    onClick={() => {
                                      if (item.dataSource) {
                                        setCurrentLayerForAttributeTable(item);
                                        setShowQueryTableDialog(true);

                                        infoboxRef.current?.close();
                                      }
                                    }}
                                  >
                                    <Box flex="1"> Attribute Table</Box>
                                    <LuTable2 />
                                  </Menu.Item>
                                )}

                                {item.type === Type.ASSET.GEOJSON_IMAGE && (
                                  <>
                                    <Menu.Item
                                      cursor={"pointer"}
                                      color={imageGeojsonMode ? "green" : "orange"}
                                      value="ImageVisibility"
                                      onClick={() => {
                                        setImageGeojsonMode(!imageGeojsonMode);
                                        setGeojsonImageCamera({
                                          1: true,
                                          2: true,
                                          3: true,
                                          4: true,
                                          5: true,
                                          6: true,
                                          7: true,
                                          8: true,
                                        });
                                      }}
                                    >
                                      <Box flex="1"> Image Visibility [{imageGeojsonMode ? "Visible" : "Hidden"}]</Box>
                                      <LuImage />
                                    </Menu.Item>
                                    <Menu.Item
                                      cursor={"pointer"}
                                      color={item.arrow_visibility ? "green" : "orange"}
                                      value="ArrowVisibility"
                                      onClick={() => {
                                        toggleArrowForMapObject(item, imageGeojson);
                                      }}
                                    >
                                      <Box flex="1"> Arrow Visibility [{item.arrow_visibility ? "Visible" : "Hidden"}]</Box>
                                      <LuArrowBigRightDash />
                                    </Menu.Item>
                                    <Menu.Item
                                      cursor={"pointer"}
                                      color={item.route_visibility ? "green" : "orange"}
                                      value="RouteVisibility"
                                      onClick={() => {
                                        for (const key in mapObjects) {
                                          if (mapObjects[key].id === item.id) {
                                            let isAdded = false;
                                            for (const key2 in mapObjects[key].inner_entities) {
                                              if (mapObjects[key].inner_entities[key2].type == "route") {
                                                isAdded = true;

                                                for (const key3 in mapObjects[key].inner_entities[key2].entities) {
                                                  viewerRef.current.entities.remove(mapObjects[key].inner_entities[key2].entities[key3]);
                                                }

                                                let filteredInnerEntities = mapObjects[key].inner_entities.filter((entity) => entity.type !== "route");
                                                mapObjects[key].inner_entities = filteredInnerEntities;
                                                setMapObjects([...mapObjects]);

                                                break;
                                              }
                                            }

                                            if (!isAdded) {
                                              let temp = addPolylineBetweenGeoJsonPoints(viewerRef.current, imageGeojson.features);
                                              for (const key in mapObjects) {
                                                if (mapObjects[key].id === item.id) {
                                                  mapObjects[key].inner_entities.push({
                                                    type: "route",
                                                    entities: [temp],
                                                  });

                                                  mapObjects[key].route_visibility = true;
                                                  setMapObjects([...mapObjects]);
                                                }
                                              }
                                            } else {
                                              mapObjects[key].route_visibility = false;
                                            }
                                            break;
                                          }
                                        }
                                      }}
                                    >
                                      <Box flex="1"> Route Visibility [{item.route_visibility ? "Visible" : "Hidden"}]</Box>
                                      <LuRoute />
                                    </Menu.Item>
                                  </>
                                )}

                                {item.type != Type.ASSET.GEOJSON_IMAGE && item.view_type != "lanelet" && item.type != Type.ASSET.THREEDTILES && item.type != Type.ASSET.OSM && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="EditStyle"
                                    onClick={() => {
                                      if (item.dataSource) {
                                        setShowStyleDialog(true);
                                        setCurrentLayer(item);
                                      }
                                    }}
                                  >
                                    <Box flex="1"> Edit Style</Box>
                                    <LuPen />
                                  </Menu.Item>
                                )}

                                {item.type == Type.ASSET.GEOJSON && item?.project_id && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="DisableLoading"
                                    onClick={() => {
                                      const temp = [...mapObjects];
                                      for (const obj of temp) {
                                        if (obj?.id == item.id) {
                                          obj.isDisableLoading = !obj.isDisableLoading;
                                          obj.is_edited = true;
                                          break;
                                        }
                                      }
                                      setMapObjects(temp);
                                    }}
                                  >
                                    <Box flex="1" color={item.isDisableLoading ? "green" : "red"}>
                                      {item.isDisableLoading ? "Enable" : "Disable"} Loading
                                    </Box>
                                    <LuShieldAlert />
                                  </Menu.Item>
                                )}

                                {item.type != Type.ASSET.GEOJSON_IMAGE && item.type != Type.ASSET.THREEDTILES && (
                                  <Menu.Item
                                    color={item.label_visible ? "green" : "orange"}
                                    cursor={"pointer"}
                                    value="LabelVisible"
                                    onClick={() => {
                                      if (item.dataSource) {
                                        toggleLabelVisibility(item);
                                      }
                                    }}
                                  >
                                    <Box flex="1"> Label Visible {getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.TOGGLE_LABEL_VISIBILITY)}</Box>
                                    {item.label_visible ? <LuEye /> : <LuEyeOff />}
                                  </Menu.Item>
                                )}

                                {item.type != Type.ASSET.GEOJSON_IMAGE && item.type != Type.ASSET.THREEDTILES && (
                                  <Menu.Item
                                    cursor={"pointer"}
                                    value="LabelStyle"
                                    onClick={() => {
                                      if (item.dataSource) {
                                        setCurrentLayer(item);
                                        setShowLabelDialog(true);
                                      }
                                    }}
                                  >
                                    <Box flex="1"> Label Style</Box>
                                    <LuTextCursor />
                                  </Menu.Item>
                                )}
                              </Menu.Content>
                            </Menu.Positioner>
                          </Menu.Root>
                        )}
                      </HStack>
                    </HStack>

                    {item.is_loading && (
                      <Progress.Root width={"full"} value={null} my={2} pr={3}>
                        <HStack gap="5">
                          <Progress.Track flex="1">
                            <Progress.Range />
                          </Progress.Track>
                          <Progress.ValueText>
                            {formatNumberForProgress(item?.progress?.start)}/{formatNumberForProgress(item?.progress?.total)}
                          </Progress.ValueText>
                        </HStack>
                      </Progress.Root>
                    )}
                  </Card.Body>
                </Card.Root>
              ))}
          </Box>
        ))}

        {Object.entries(
          mapObjectsAssest
            .sort((a, b) => String(a.project_id ?? "").localeCompare(String(b.project_id ?? "")))
            .reduce((acc, item) => {
              const pid = String(item.project_id ?? "NoProject");
              if (!acc[pid]) acc[pid] = [];
              acc[pid].push(item);
              return acc;
            }, {}),
        ).map(([projectId, layers]) => (
          <Box key={projectId} border="1px solid #ccc" bg={"blackAlpha.100"} borderRadius="md" p={2} mb={3}>
            <Text fontWeight="bold" fontSize="sm" mb={1}>
              Project: {projects.find((p) => String(p.id) === String(projectId))?.project_name || `Unknown (${projectId})`}
            </Text>

            {layers.map((item, index) => (
              <Card.Root mb={2} key={index} size="sm" bg={"white"} cursor="pointer">
                <Card.Header py={0} pt={1} pr={2}>
                  <HStack justify={"space-between"}>
                    <HStack>
                      <Editable.Root disabled value={removeFileExtension(item?.file_name)} activationMode="dblclick" width={"fit-content"} fontWeight={"medium"}>
                        <Editable.Preview
                          maxWidth={"220px"}
                          style={{
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                            textAlign: "start",
                          }}
                        />
                        <Editable.Input maxWidth={"220px"} />
                      </Editable.Root>
                    </HStack>
                  </HStack>
                </Card.Header>
                <Card.Body color="fg.muted" py={3} pr={0}>
                  <HStack width={"full"} justify={"space-between"}>
                    <HStack>
                      {["Point", "MultiPoint"].includes(getGeometryTypeGeojson(item.geojson)) && <LuCircle />}
                      {["LineString", "MultiLineString"].includes(getGeometryTypeGeojson(item.geojson)) && <LuSpline />}
                      {["Polygon", "MultiPolygon"].includes(getGeometryTypeGeojson(item.geojson)) && <LuDiamond />}
                      {item.type == Type.ASSET.THREEDTILES && <LuBox />}
                      {item.style_outline_color && <ColorSwatch value={item.style_outline_color} />}

                      <Text fontSize={"12px"} lineClamp={1}>
                        {item.type == Type.ASSET.THREEDTILES ? "3D Tiles" : item.type}
                      </Text>
                    </HStack>
                  </HStack>

                  {item.is_loading && (
                    <Progress.Root width={"full"} value={null} my={2} pr={3}>
                      <HStack gap="5">
                        <Progress.Track flex="1">
                          <Progress.Range />
                        </Progress.Track>
                        <Progress.ValueText>
                          {formatNumberForProgress(item?.progress?.start)}/{formatNumberForProgress(item?.progress?.total)}
                        </Progress.ValueText>
                      </HStack>
                    </Progress.Root>
                  )}
                </Card.Body>
              </Card.Root>
            ))}
          </Box>
        ))}
      </Box>
      {editMode && <LayersEdit />}
      <LayersStyleDialog />
      <LayersLabelDialog />
      <LayersAttributeTable />
      <LayersEditFeatureDialog />
      <LayersBultEditFeatureDialog />
      <LayersDeleteDialog />
      <LayersExport />
      <CreateProjectDialog />

      <LayersEditCoordinate />
      <LayersFilterDialog />
      <LayersCompareWith />
      <LayersSave />
      <LayersSwitch />
      <LayersApprovalGenerateRoute />
      <LayersApprovalScanRoute />
      <LayersCopyToProject />
      <LayersMoveToAnotherLayer />
      <LayersApprovalLoadLayers />
      <LayersChartCZML />
      <JsonEditor />
      <GeojsonFilterDialog />
      <GeojsonFilterOnAllDialog />
      <LayersRoadSlopeDialog />
      <FindBreakPoints />
      <LayersListFromServer />
      <LayersMerge />
      <LayersImportFromSQL />
      <ChangeSQLQuery />
      <Portal>
        <ImageGeojsonBoxes />
      </Portal>
    </>
  );
}

export default Layers;
